/* Variant A v2 — "Editorial Pro, refined"
 * Button hierarchy collapsed per the 6-point critique:
 *  1. Primary CTA (新增交易) isolated, left as the only heavy action.
 *  2. Refresh is now a passive "last sync" indicator + tiny icon.
 *  3. Quick links collapse into an app-switcher popover.
 *  4. Discord/ERP entry points de-duplicated (sidebar only for ERP;
 *     Discord lives as a single bottom-right floating tab).
 *  5. Row operations are hover-reveal + single ⋯ menu.
 *  6. "方向" uses text+color no fill; "狀態" uses dot+text no fill.
 *  Plus: filter/sort/tabs consolidated into one table toolbar.
 */

const VA2 = {
  bg:         '#F6F4EF',
  surface:    '#FFFFFF',
  ink:        '#0E1411',
  inkSoft:    '#4A5048',
  inkFaint:   '#8B8F89',
  rule:       '#E5E2DA',
  ruleSoft:   '#EEEBE3',
  accent:     '#0B5F5A',
  accentSoft: '#E6EEED',
  up:         '#0B5F5A',
  down:       '#9B3330',
  warn:       '#A56A1F',
  sansDisplay:'"Söhne","Inter","Helvetica Neue",system-ui,sans-serif',
  serif:      '"Source Serif 4","GT Sectra","Fraunces",Georgia,serif',
  mono:       '"JetBrains Mono","SF Mono",Menlo,monospace',
};
if (window.GxCore) window.GxCore.adoptTokens(VA2, 'VA2');  // Expressive only (no-op under Bento): fonts + colour keys → var(--gx-*) (SPEC §1.2 內頁換膚)

/* Market-direction colour (price change / P&L sign / 多空 side) — SPEC §1.2.
 * Bento: returns `bento` unchanged (the original literal expression, e.g. VA2.up / VA2.down).
 * Expressive: Taiwan convention — up RED (GxT.color.mktUp), down GREEN (GxT.color.mktDown).
 * Market direction ONLY: errors, delete buttons, punch states keep VA2.down / VA2.up directly.
 * gx-mkt:va2Mkt:begin */
function va2Mkt(bento, up) { return uiPick(bento, up ? GxT.color.mktUp : GxT.color.mktDown); }
/* gx-mkt:va2Mkt:end */

/* Trading Desk masthead date, e.g. "2026 October 16, Friday" — always the Asia/Taipei calendar day
 * (SPEC §6), independent of the viewer's machine timezone. `now` = Date or epoch ms (default: now). */
function va2DeskDateLabel(now) {
  const d = now instanceof Date ? now : new Date(typeof now === 'number' ? now : Date.now());
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'long', timeZone: 'Asia/Taipei',
  }).formatToParts(d).forEach((p) => { parts[p.type] = p.value; });
  return `${parts.year} ${parts.month} ${parts.day}, ${parts.weekday}`;
}

/* Greeting salutation by Taipei hour — same cutoffs as Expressive home hmSalute()
 * (expressive-home.jsx): < 11 早安, < 18 午安, else 晚安. Keep the two in sync. */
function va2Salute(now) {
  const ms = now instanceof Date ? now.getTime() : (typeof now === 'number' ? now : Date.now());
  const hr = new Date(ms + 8 * 3600 * 1000).getUTCHours();   // TPE hour
  if (hr < 11) return '早安';
  if (hr < 18) return '午安';
  return '晚安';
}

/* 分析報告微服務對外網址 — 獨立 repo / 獨立容器，比照台股掃描器（原亞當分析）以外部連結嵌入。
 * 連結會帶上 portal 的 JWT（?token=），report-service 用相同 JWT_SECRET 驗簽。
 * 換環境時改這裡即可。 */
const REPORT_SERVICE_URL = 'https://report.clawai.com.tw';

/* Market ticker：呼叫 /api/market/quotes，每 30s refresh */
function MarketTicker() {
  // symbol → 顯示中文標籤
  const SPECS = [
    { sym: '^TWII',  label: '加權' },
    { sym: 'WTX&',   label: '台指近' },
    { sym: 'WMT&',   label: '小台近' },
    { sym: 'TWD=X',  label: '美元' },
  ];
  const [quotes, setQuotes] = React.useState(null);   // null = loading

  React.useEffect(() => {
    const url = '/api/market/stream?symbols=' + encodeURIComponent(SPECS.map(s => s.sym).join(','))
      + '&token=' + encodeURIComponent(localStorage.getItem('token') || '');
    const es = new EventSource(url);
    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        setQuotes(data.quotes || []);
      } catch {}
    };
    es.onerror = () => { setQuotes(q => q === null ? [] : q); };
    return () => es.close();
  }, []);

  return (
    <div className="va2-market-ticker" style={{ display: 'flex', gap: 20, alignItems: 'center', flex: 1, fontFamily: VA2.mono, fontSize: FontSizes.trading_xl }}>
      {SPECS.map(({ sym, label }) => {
        const q = quotes && quotes.find(x => x.symbol === sym);
        const up = q && q.changePercent >= 0;
        const priceStr = q && q.price != null
          ? (q.price >= 1000 ? q.price.toLocaleString() : q.price.toFixed(q.price < 100 ? 3 : 2))
          : '—';
        const pctStr = q && q.changePercent != null
          ? (q.changePercent >= 0 ? '+' : '') + q.changePercent.toFixed(2) + '%'
          : '';
        return (
          <div key={sym} style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span style={{ color: VA2.inkFaint, fontSize: FontSizes.trading_md, letterSpacing: '0.05em' }}>{label}</span>
            <span style={{ color: VA2.ink, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{priceStr}</span>
            {pctStr && <span style={{ color: va2Mkt(up ? VA2.up : VA2.down, up), fontSize: FontSizes.trading_md }}>{pctStr}</span>}
          </div>
        );
      })}
      {quotes === null && <span style={{ color: VA2.inkFaint, fontSize: FontSizes.trading_md }}>載入中…</span>}
    </div>
  );
}

/* ─────────────────────────  TraderRightRail  ───────────────────
 * 280px sticky right column on viewports ≥ 1480px. Matches ERP's RightRail
 * pattern but with trader-relevant widgets (avoids duplicating the top
 * ticker — that's already in the header).
 *
 *  Widgets (top → bottom):
 *    1. QuickPunchMini  — dynamic employee clock-in/out tool
 *    2. TopMovers       — best/worst PnL traders from TRADERS data
 *    3. WatchlistMini   — placeholder for personal symbols
 */
function QuickPunchMini() {
  const [now, setNow] = React.useState(new Date());
  const [myEmp, setMyEmp] = React.useState(null);
  const [todayPunches, setTodayPunches] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [confirmOut, setConfirmOut] = React.useState(false);   // clock-out confirm (SPEC §5.1)
  const toast = typeof useToast !== 'undefined' ? useToast() : null;

  // Time ticking
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch employee details and today's punches
  const fetchData = async () => {
    try {
      let emp = myEmp;
      if (!emp) {
        emp = await api('GET', '/employees/me').catch(() => null);
        setMyEmp(emp);
      }
      if (emp && emp.id) {
        const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
        const res = await api('GET', `/attendance/punches?employee_id=${emp.id}&from_date=${today}&to_date=${today}&page_size=20`).catch(() => ({ items: [] }));
        setTodayPunches(res.items || []);
      }
    } catch (e) {
      console.error('QuickPunch fetch failed:', e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
    const handlePunchUpdate = () => {
      fetchData();
    };
    window.addEventListener('roster:updated', handlePunchUpdate);
    return () => {
      window.removeEventListener('roster:updated', handlePunchUpdate);
    };
  }, [myEmp?.id]);

  const handlePunchIn = async () => {
    if (!myEmp || !myEmp.id || busy) return;
    setBusy(true);
    try {
      await api('POST', '/attendance/punches', { employee_id: myEmp.id, remark: null });
      if (toast) toast('已上班打卡，辛苦了！', 'ok');
      await fetchData();
      window.dispatchEvent(new CustomEvent('roster:updated'));
    } catch (e) {
      if (toast) toast(e.detail || '打卡失敗', 'error');
      else alert(e.detail || '打卡失敗');
    } finally {
      setBusy(false);
    }
  };

  // Runs only from the PunchOutConfirm dialog — the button just opens it (clock-in stays one tap).
  const handlePunchOut = async () => {
    const openPunch = todayPunches.find(p => !p.abort_time);
    if (!openPunch) { setConfirmOut(false); return; }
    if (busy) return;
    setBusy(true);
    try {
      await api('POST', `/attendance/punches/${openPunch.id}/close`);
      if (toast) toast('下班打卡成功，辛苦了！', 'ok');
      await fetchData();
      window.dispatchEvent(new CustomEvent('roster:updated'));
    } catch (e) {
      if (toast) toast(e.detail || '打卡失敗', 'error');
      else alert(e.detail || '打卡失敗');
    } finally {
      setBusy(false);
      setConfirmOut(false);
    }
  };

  const openPunch = todayPunches.find(p => !p.abort_time);
  const isCheckedIn = !!openPunch;
  const askPunchOut = () => { if (isCheckedIn && !busy) setConfirmOut(true); };

  const p = (n) => String(n).padStart(2, '0');
  const clockStr = `${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
  const dateStr = now.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });

  let elapsedStr = '';
  if (isCheckedIn && openPunch.punch_time) {
    const elapsedMs = Math.max(0, now - new Date(openPunch.punch_time));
    const totalSec = Math.floor(elapsedMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    elapsedStr = `${p(h)}:${p(m)}:${p(s)}`;
  }

  const completedPunches = todayPunches.filter(p => !!p.abort_time);
  const totalCompletedMins = completedPunches.reduce((sum, p) => sum + (p.work_minutes || 0), 0);
  const compH = Math.floor(totalCompletedMins / 60);
  const compM = totalCompletedMins % 60;
  const totalCompletedStr = totalCompletedMins > 0 ? `今日已完成工時：${compH} 小時 ${compM} 分鐘` : '';

  if (loading) {
    return (
      <div style={{
        padding: '14px 16px', background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
      }}>
        <div style={{ fontSize: FontSizes.trading_xs, color: VA2.inkFaint, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
          快速打卡
        </div>
        <div style={{ fontSize: FontSizes.trading_md, color: VA2.inkFaint, fontStyle: 'italic' }}>載入中…</div>
      </div>
    );
  }

  if (!myEmp) {
    return (
      <div style={{
        padding: '14px 16px', background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
      }}>
        <div style={{ fontSize: FontSizes.trading_xs, color: VA2.inkFaint, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
          快速打卡
        </div>
        <div style={{ fontSize: FontSizes.trading_md, color: VA2.down }}>
          尚未綁定員工帳號
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '14px 16px', background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: VA2.inkFaint,
        letterSpacing: '0.14em', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <span>快速打卡</span>
        <span style={{ fontSize: FontSizes.trading_xxs, color: isCheckedIn ? VA2.up : VA2.inkFaint, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: isCheckedIn ? VA2.up : VA2.inkFaint,
            boxShadow: isCheckedIn ? `0 0 4px ${GxCore.alpha(VA2.up, '88')}` : 'none',
          }} />
          {isCheckedIn ? '上班中' : '未打卡'}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', padding: '6px 0', borderBottom: `1px dashed ${VA2.ruleSoft}` }}>
        <div style={{ fontFamily: VA2.mono, fontSize: FontSizes.lg, fontWeight: 600, color: VA2.ink, fontVariantNumeric: 'tabular-nums' }}>
          {clockStr}
        </div>
        <div style={{ fontSize: FontSizes.trading_xxs, color: VA2.inkFaint }}>
          {dateStr}
        </div>
      </div>

      <div style={{ fontSize: FontSizes.trading_xs, color: VA2.inkSoft, textAlign: 'center', minHeight: 16 }}>
        {isCheckedIn ? (
          <div>
            已上班：<strong style={{ fontFamily: VA2.mono, color: VA2.accent, fontVariantNumeric: 'tabular-nums' }}>{elapsedStr}</strong>
          </div>
        ) : (
          <div>
            {totalCompletedStr || '今日尚未開始打卡'}
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={isCheckedIn ? askPunchOut : handlePunchIn}
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: 6,
          background: isCheckedIn ? VA2.down : VA2.accent,
          color: '#FFFFFF',
          border: 'none',
          cursor: busy ? 'not-allowed' : 'pointer',
          fontWeight: 600,
          fontSize: FontSizes.trading_md,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          boxShadow: isCheckedIn ? `0 2px 6px ${GxCore.alpha(VA2.down, '22')}` : `0 2px 6px ${GxCore.alpha(VA2.accent, '22')}`,
          transition: 'all 150ms ease-in-out',
        }}
        onMouseEnter={(e) => {
          if (!busy) {
            e.currentTarget.style.opacity = '0.9';
            e.currentTarget.style.transform = 'translateY(-0.5px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!busy) {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <Icon name="clock" size={12} stroke={2.5} />
        {isCheckedIn ? '下班打卡' : '上班打卡'}
      </button>
      <PunchOutConfirm open={confirmOut && isCheckedIn} busy={busy}
        onCancel={() => setConfirmOut(false)} onConfirm={handlePunchOut} />
    </div>
  );
}

function TopMoversWidget() {
  // Read from window.TRADERS (set by index.html bootstrap)
  const traders = (typeof TRADERS !== 'undefined' ? TRADERS : []).slice();
  const sorted = traders.slice().sort((a, b) => (b.pnl || 0) - (a.pnl || 0));
  const top = sorted.slice(0, 3);
  const bottom = sorted.slice(-2).reverse();
  if (traders.length === 0) {
    return (
      <div style={{
        padding: '14px 16px', background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
      }}>
        <div style={{
          fontSize: FontSizes.trading_xs, color: VA2.inkFaint,
          letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8,
        }}>Top Movers</div>
        <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic' }}>無交易員資料</div>
      </div>
    );
  }
  const Row = ({ t, isUp }) => (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      padding: '4px 0', fontSize: FontSizes.xxs,
    }}>
      <span style={{ color: VA2.ink, fontWeight: 500 }}>{t.name}</span>
      <span style={{
        fontFamily: VA2.mono, fontWeight: 500,
        color: va2Mkt(isUp ? VA2.up : VA2.down, isUp),
        fontVariantNumeric: 'tabular-nums', fontSize: FontSizes.trading_xl,
      }}>{(t.pnl >= 0 ? '+' : '−') + '$' + Math.abs(t.pnl).toLocaleString()}</span>
    </div>
  );
  return (
    <div style={{
      padding: '14px 16px', background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: VA2.inkFaint,
        letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8,
        display: 'flex', justifyContent: 'space-between',
      }}>
        <span>Top Movers</span>
        <span style={{ color: va2Mkt(VA2.up, true) }}>↑</span>
      </div>
      {top.map(t => <Row key={t.name} t={t} isUp={t.pnl >= 0} />)}
      {bottom.length > 0 && (
        <>
          <div style={{ height: 1, background: VA2.ruleSoft, margin: '8px 0 6px' }} />
          <div style={{ fontSize: FontSizes.trading_xs, color: VA2.inkFaint, marginBottom: 4, letterSpacing: '0.08em' }}>
            BOTTOM
          </div>
          {bottom.map(t => <Row key={t.name} t={t} isUp={t.pnl >= 0} />)}
        </>
      )}
    </div>
  );
}

function WatchlistMini() {
  // Static placeholder — extend later by reading from /api/market/quotes
  const items = [
    { sym: '2330', name: '台積電',  price: '1,045',  delta: '+1.8%', up: true },
    { sym: '2317', name: '鴻海',    price: '208',    delta: '+0.9%', up: true },
    { sym: '2454', name: '聯發科',  price: '1,335',  delta: '−0.5%', up: false },
    { sym: '3008', name: '大立光',  price: '2,415',  delta: '−1.2%', up: false },
  ];
  return (
    <div style={{
      padding: '14px 16px', background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: VA2.inkFaint,
        letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8,
        display: 'flex', justifyContent: 'space-between',
      }}>
        <span>Watchlist</span>
        <span style={{
          fontSize: FontSizes.trading_3xs, padding: '1px 5px', borderRadius: 2,
          background: '#FAEFD8', color: '#A56A1F', fontFamily: VA2.mono, letterSpacing: '0.05em',
        }}>MOCK</span>
      </div>
      {items.map(it => (
        <div key={it.sym} style={{
          display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 8, alignItems: 'baseline',
          padding: '5px 0', fontSize: FontSizes.trading_xl,
        }}>
          <span style={{ fontFamily: VA2.mono, color: VA2.inkFaint, fontSize: FontSizes.trading_md }}>{it.sym}</span>
          <span style={{ color: VA2.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
          <span style={{
            fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums',
            color: va2Mkt(it.up ? VA2.up : VA2.down, it.up), fontWeight: 500,
          }}>{it.delta}</span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────  Universal right-rail widgets  ──────────────
 * After the unified-shell refactor, the right rail is route-agnostic:
 * same widgets on the trading dashboard and on every ERP / workspace
 * page. TopMovers / Watchlist were retired (trader-only context).
 * Today + Notifications are mock for now — wire to real APIs later. */
function TodayMini() {
  const [now, setNow] = React.useState(new Date());
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Refresh once per minute so the date flips at midnight
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const weekday = ['日','一','二','三','四','五','六'][now.getDay()];
  const dateStr = now.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric' });

  // Get local date string YYYY-MM-DD
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;

  React.useEffect(() => {
    let alive = true;
    const loadTodayData = async () => {
      try {
        setLoading(true);
        // 1. Fetch today's calendar events
        const calResp = await api('GET', `/calendar/events?from_date=${todayStr}&to_date=${todayStr}&page_size=100`).catch(() => ({ items: [] }));
        
        // 2. Fetch approved leave requests (HR/Admin will see all, manager sees subordinates, employee sees self)
        const leaveResp = await api('GET', `/leave/requests?status=approved&page_size=100`).catch(() => ({ items: [] }));
        
        if (!alive) return;
        
        const combined = [];
        
        // Process calendar events
        if (calResp && Array.isArray(calResp.items)) {
          calResp.items.forEach(ev => {
            let timeStr = '全天';
            if (ev.start_at && !ev.all_day) {
              const dt = new Date(ev.start_at);
              const hrs = String(dt.getHours()).padStart(2, '0');
              const mins = String(dt.getMinutes()).padStart(2, '0');
              timeStr = `${hrs}:${mins}`;
            }
            combined.push({
              time: timeStr,
              title: ev.title || '無標題事件',
              kind: 'event',
              color: VA2.accent
            });
          });
        }
        
        // Process leaves — 比完整 timestamp（不可切 UTC 字串取日期，見 SPEC §6 時區規約）：
        //   進行中（start <= now < end）→「請假中」；當日稍後才開始 → 帶開始時間的項目。
        if (leaveResp && Array.isArray(leaveResp.items)) {
          const nowTs = new Date();
          // 「當日」判斷用 TPE 日期 + 當日邊界（假單 start/end 為 UTC 儲存）
          const tpeToday = nowTs.toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
          const dayStart = new Date(`${tpeToday}T00:00:00+08:00`);
          const dayEnd = new Date(`${tpeToday}T23:59:00+08:00`);
          const tpeHM = (d) => d.toLocaleTimeString('en-GB', {
            timeZone: 'Asia/Taipei', hour: '2-digit', minute: '2-digit', hour12: false,
          });
          leaveResp.items.forEach(r => {
            if (!r.start_at || !r.end_at) return;
            const s = new Date(r.start_at);
            const e = new Date(r.end_at);
            if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return;
            const name = r.employee_name || '同仁';
            const typeName = r.leave_type_name || '假勤';
            if (s <= nowTs && nowTs < e) {
              // 進行中 — 覆蓋整個 TPE 當日（全天/純日期假：start 00:00 且 end 23:59 或跨日）
              // 顯示「請假中（全天）」；時段假顯示今日內的時間區間
              const allDay = s <= dayStart && e >= dayEnd;
              const clipS = s > dayStart ? s : dayStart;
              const clipE = e < dayEnd ? e : dayEnd;
              combined.push({
                time: '請假',
                sortKey: '00:00',  // 進行中排在稍後開始的假之前
                title: allDay
                  ? `${name} 請假中（全天）(${typeName})`
                  : `${name} 請假中 (${typeName}) ${tpeHM(clipS)}–${tpeHM(clipE)}`,
                kind: 'leave',
                color: VA2.warn
              });
            } else if (nowTs < s && s <= dayEnd) {
              // 當日（TPE）稍後才開始的假 → 帶開始時間的項目
              combined.push({
                time: tpeHM(s),
                title: `${name} 請假 (${typeName})`,
                kind: 'leave',
                color: VA2.warn
              });
            }
          });
        }

        // Sort: leaves first, then by time (leave 用 sortKey 讓「請假中」優先於稍後開始者)
        combined.sort((a, b) => {
          if (a.kind === 'leave' && b.kind !== 'leave') return -1;
          if (a.kind !== 'leave' && b.kind === 'leave') return 1;
          return (a.sortKey || a.time).localeCompare(b.sortKey || b.time);
        });
        
        setEvents(combined);
      } catch (e) {
        console.error('Failed to load today mini widget:', e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    
    loadTodayData();
    return () => { alive = false; };
  }, [todayStr]);

  return (
    <div style={{
      padding: '14px 16px', background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
      display: 'flex', flexDirection: 'column', flex: '1 1 0%', minHeight: 120,
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: VA2.inkFaint,
        letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10,
        display: 'flex', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <span>今日</span>
        <span style={{ fontFamily: VA2.mono }}>{dateStr} · 週{weekday}</span>
      </div>
      <div className="v2-scroll" style={{ flex: 1, overflowY: 'auto', margin: '0 -4px' }}>
        {loading ? (
          <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic', padding: '0 4px' }}>載入中…</div>
        ) : events.length === 0 ? (
          <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic', padding: '0 4px' }}>今日無事件</div>
        ) : events.map((e, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '50px 1fr', gap: 10, alignItems: 'baseline',
            padding: '5px 4px', fontSize: FontSizes.xxs,
          }}>
            <span style={{
              fontFamily: VA2.mono, color: e.color || VA2.inkFaint, fontSize: FontSizes.trading_md,
              fontVariantNumeric: 'tabular-nums', fontWeight: 500
            }}>{e.time}</span>
            <span style={{ color: VA2.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* 相對時間格式 — NotificationsMini 與 NotificationsBell 共用。 */
function formatRelativeTs(isoStr) {
  if (!isoStr) return '';
  try {
    const diffMs = new Date() - new Date(isoStr);
    const diffMins = Math.floor(diffMs / 60_000);
    if (diffMins < 1) return '剛剛';
    if (diffMins < 60) return `${diffMins}分鐘前`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}小時前`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}天前`;
    return isoStr.slice(5, 10);
  } catch {
    return '';
  }
}

function NotificationsMini() {
  const [notifications, setNotifications] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  // SSE：通知變化即 refetch。useServerEvent 由 hr-shell.js 匯出；
  // variant-a-v2.js 先載入但 render 發生在全部 script 載完後，此時必已存在。
  const sseEvt = window.useServerEvent('notifications.changed');

  React.useEffect(() => {
    let alive = true;
    const loadNotifications = async () => {
      // 403（analyst / non_staff 非員工帳號）或未登入 → 靜默降級為「無新通知」
      const res = await api('GET', '/notifications?page_size=5').catch(() => null);
      if (!alive) return;
      const items = res && Array.isArray(res.items) ? res.items : [];
      setNotifications(items.map(n => ({
        id: n.id,
        title: n.title || '通知',
        text: n.body || '',
        ts: n.created_at,
        href: n.link || '#/',
        is_read: n.is_read,
        // 待審核（*.submitted 類事件）→ 沿用原本「可行動」的醒目視覺
        isActionable: /\.submitted$/.test(n.kind || ''),
      })));
      setLoading(false);
    };

    loadNotifications();
    const t = setInterval(loadNotifications, 30_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [sseEvt]);

  return (
    <div style={{
      padding: '14px 16px', background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
      display: 'flex', flexDirection: 'column', flex: '1 1 0%', minHeight: 150,
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: VA2.inkFaint,
        letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10,
        display: 'flex', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <span>通知</span>
        {notifications.length > 0 && (
          <span style={{
            fontSize: FontSizes.trading_xxs, padding: '1px 5px', borderRadius: 2,
            background: VA2.accent, color: '#fff', fontFamily: VA2.mono, letterSpacing: '0.05em',
          }}>{notifications.length}</span>
        )}
      </div>
      <div className="v2-scroll" style={{ flex: 1, overflowY: 'auto', margin: '0 -4px' }}>
        {loading ? (
          <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic', padding: '0 4px' }}>載入中…</div>
        ) : notifications.length === 0 ? (
          <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic', padding: '0 4px' }}>無新通知</div>
        ) : notifications.map((n, i) => (
          <a key={n.id} href={n.href}
          onClick={() => {
            // fire-and-forget 標已讀（403 / 網路錯誤靜默忽略）
            if (!n.is_read) api('POST', '/notifications/' + n.id + '/read').catch(() => {});
          }}
          style={{
            display: 'block', textDecoration: 'none', color: 'inherit',
            padding: '8px 4px', borderTop: i > 0 ? `1px solid ${VA2.ruleSoft}` : 'none',
            cursor: 'pointer',
            transition: 'background 120ms',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.01)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
              <span style={{ 
                fontSize: FontSizes.trading_xs, 
                color: n.isActionable ? VA2.warn : VA2.inkSoft,
                fontWeight: 600,
                letterSpacing: '0.04em'
              }}>
                {n.title}
              </span>
              <span style={{ fontSize: FontSizes.trading_sm, color: VA2.inkFaint, fontFamily: VA2.mono }}>
                {formatRelativeTs(n.ts)}
              </span>
            </div>
            <div style={{ color: VA2.ink, fontSize: FontSizes.xxs, lineHeight: 1.4, fontWeight: n.isActionable ? 500 : 400 }}>
              {n.text}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────  NotificationsBell  ─────────────────
 * Header 通知鈴鐺：未讀數 badge（30s polling + SSE notifications.changed）
 * + 右對齊 dropdown 通知清單（最近 15 則、單筆/全部標已讀）。
 * 403（analyst / non_staff 非員工帳號）或未登入 → 靜默降級，badge 不顯示。
 */
function NotificationsBell() {
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const ref = React.useRef(null);
  const aliveRef = React.useRef(true);
  React.useEffect(() => () => { aliveRef.current = false; }, []);
  // SSE：useServerEvent 由 hr-shell.js 匯出（render 時全部 script 已載完，必存在）
  const sseEvt = window.useServerEvent('notifications.changed');
  const openRef = React.useRef(open);
  openRef.current = open;

  // Badge：mount + 每 30s polling + SSE 事件 → refetch 未讀數
  React.useEffect(() => {
    const load = () => {
      api('GET', '/notifications/unread-count')
        .then(r => {
          if (aliveRef.current) setUnreadCount(r && typeof r.count === 'number' ? r.count : 0);
        })
        .catch(() => {}); // 403 / 未登入 → 靜默降級，badge 不顯示
    };
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [sseEvt]);

  const loadItems = () => {
    setLoading(true);
    api('GET', '/notifications?page_size=15')
      .then(r => { if (aliveRef.current) setItems(r && Array.isArray(r.items) ? r.items : []); })
      .catch(() => { if (aliveRef.current) setItems([]); })
      .finally(() => { if (aliveRef.current) setLoading(false); });
  };

  // 開啟面板 → 載入清單
  React.useEffect(() => {
    if (open) loadItems();
  }, [open]);

  // SSE 事件時若面板開著 → 一併 refetch 清單（badge 已由上面的 effect 處理）
  React.useEffect(() => {
    if (sseEvt && openRef.current) loadItems();
  }, [sseEvt]);

  // 關閉：click-outside + Escape
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleItemClick = (n) => {
    if (!n.is_read) {
      // 樂觀更新：立即標已讀、未讀數 -1，再 fire-and-forget 打 API
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      setUnreadCount(c => Math.max(0, c - 1));
      api('POST', '/notifications/' + n.id + '/read').catch(() => {});
    }
    location.hash = n.link || '#/';
    setOpen(false);
  };

  const handleReadAll = () => {
    setUnreadCount(0);
    setItems(prev => prev.map(x => x.is_read ? x : { ...x, is_read: true }));
    api('POST', '/notifications/read-all').catch(() => {});
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={'通知' + (unreadCount > 0 ? `（${unreadCount} 則未讀）` : '')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative', padding: 6, color: VA2.inkSoft, cursor: 'pointer',
          background: 'transparent', border: 'none', display: 'inline-flex', alignItems: 'center',
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = VA2.ink}
        onMouseLeave={(e) => e.currentTarget.style.color = VA2.inkSoft}
      >
        <Icon name="bell" size={15} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: 0, minWidth: 14, height: 14,
            padding: '0 3px', borderRadius: 7, boxSizing: 'border-box',
            background: VA2.down, color: uiPick('#fff', GxT.color.onP), boxShadow: '0 0 0 2px ' + VA2.bg,
            fontSize: FontSizes.trading_xxs, fontFamily: VA2.mono, fontWeight: 600,
            lineHeight: '14px', textAlign: 'center', pointerEvents: 'none',
          }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8, zIndex: 60,
          width: 340, maxHeight: 400, display: 'flex', flexDirection: 'column',
          background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6,
          boxShadow: uiPick('0 8px 28px rgba(0,0,0,0.12)', GxT.shadow.menu),
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderBottom: `1px solid ${VA2.ruleSoft}`, flexShrink: 0,
          }}>
            <span style={{
              fontSize: FontSizes.trading_md, color: VA2.inkFaint, fontWeight: 600,
              letterSpacing: '0.12em', textTransform: 'uppercase',
            }}>
              通知{unreadCount > 0 ? ` · ${unreadCount > 99 ? '99+' : unreadCount} 未讀` : ''}
            </span>
            <button
              type="button"
              onClick={handleReadAll}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '2px 4px', color: VA2.accent, fontSize: FontSizes.trading_md,
              }}
            >全部標為已讀</button>
          </div>
          <div className="v2-scroll" style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic', padding: '12px 14px' }}>載入中…</div>
            ) : items.length === 0 ? (
              <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic', padding: '12px 14px' }}>沒有通知</div>
            ) : items.map((n, i) => (
              <div key={n.id}
                onClick={() => handleItemClick(n)}
                style={{
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                  padding: '9px 14px', cursor: 'pointer',
                  borderTop: i > 0 ? `1px solid ${VA2.ruleSoft}` : 'none',
                  transition: 'background 120ms',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = uiPick('rgba(0,0,0,0.02)', GxT.color.stateMenu)}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', marginTop: 4, flexShrink: 0,
                  background: n.is_read ? 'transparent' : VA2.accent,
                }}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                    <span style={{
                      fontSize: FontSizes.trading_xs, letterSpacing: '0.04em',
                      color: n.is_read ? VA2.inkSoft : VA2.ink,
                      fontWeight: n.is_read ? 400 : 700,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{n.title || '通知'}</span>
                    <span style={{ fontSize: FontSizes.trading_sm, color: VA2.inkFaint, fontFamily: VA2.mono, flexShrink: 0 }}>
                      {formatRelativeTs(n.created_at)}
                    </span>
                  </div>
                  {n.body ? (
                    <div style={{
                      color: VA2.inkSoft, fontSize: FontSizes.xxs, lineHeight: 1.4,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>{n.body}</div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────  WorkspaceHeader  ───────────────────
 * Top bar shared by every route in the unified shell (trading dashboard
 * + all ERP / workspace pages). Contains:
 *   • MarketTicker  — live quotes, refreshes every 30s
 *   • Today's date  — refreshes every minute (handles midnight rollover)
 *   • AppSwitcher   — quick-launch dropdown (redundant with sidebar but fast)
 *   • NotificationsBell — 通知鈴鐺（未讀 badge + dropdown 清單）
 *   • User badge    — avatar + name + logout
 */
function WorkspaceHeader({ hideTicker = false } = {}) {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  const dateStr = now.toLocaleDateString('zh-TW', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
  });
  return (
    <><header style={{
      display: 'flex', alignItems: 'center', padding: '14px 40px', gap: 20,
      borderBottom: `1px solid ${VA2.rule}`, background: VA2.bg, fontSize: FontSizes.xxs,
    }}>
      <button
        type="button"
        className="va2-hamburger-btn"
        onClick={() => window.dispatchEvent(new Event('toggle-sidebar'))}
        title="打開導覽選單"
      >
        <Icon name="menu" size={16} />
      </button>
      {hideTicker ? <div style={{ flex: 1 }} /> : <MarketTicker />}

      {/* 全站搜尋入口（hr-cmdk.jsx；點了開 Cmd+K 面板） */}
      {window.CommandSearchButton && <window.CommandSearchButton />}

      <div style={{ width: 1, height: 16, background: VA2.rule }} />

      <span
        title="今日"
        style={{
          fontFamily: VA2.sansDisplay, fontSize: FontSizes.trading_xl, color: VA2.inkSoft,
          fontWeight: 450, whiteSpace: 'nowrap',
        }}
      >{dateStr}</span>

      <div style={{ width: 1, height: 16, background: VA2.rule }} />

      <AppSwitcher />

      {/* 通知鈴鐺：受限帳號（analyst / analyst_lite / non_staff，無 privileged role）
       * 整個不渲染——後端 /api/notifications 對非員工一律 403，前端降級不顯示、
       * 白名單不為此開洞（SPEC §11.5）。不 mount 也就不啟動 30 秒輪詢。 */}
      {!(typeof window.isRestrictedAccount === 'function' && window.isRestrictedAccount(window._USER)) && (
        <NotificationsBell />
      )}

      {/* User identity moved to sidebar bottom (Notion / Linear pattern).
       * Header is reserved for ambient context: market ticker, date, bell. */}
    </header>
    {window.UpdatesBanner && <window.UpdatesBanner />}
    </>
  );
}

function TraderRightRail() {
  const [roster, setRoster] = React.useState(null);

  const fetchRoster = () => {
    fetch('/api/attendance/roster', {
      headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') },
    })
      .then(r => r.ok ? r.json() : [])
      .then(list => { setRoster(Array.isArray(list) ? list : []); })
      .catch(() => { setRoster([]); });
  };

  React.useEffect(() => {
    fetchRoster();
    const handleRosterUpdate = () => {
      fetchRoster();
    };
    window.addEventListener('roster:updated', handleRosterUpdate);
    return () => {
      window.removeEventListener('roster:updated', handleRosterUpdate);
    };
  }, []);

  return (
    <aside className="va2-right-rail" style={{
      padding: '20px 16px 24px', display: 'flex', flexDirection: 'column', gap: 12,
      position: 'sticky', top: 0, alignSelf: 'start', height: '100vh',
      overflow: 'hidden', borderLeft: `1px solid ${VA2.rule}`,
      background: VA2.bg,
    }}>

      <QuickPunchMini />
      <TodayMini />
      <NotificationsMini />

      {/* Colleagues Online Widget moved to right rail */}
      <div style={{ borderTop: `1px solid ${VA2.rule}`, paddingTop: 12, marginTop: 4, display: 'flex', flexDirection: 'column', flex: '1 1 0%', minHeight: 120 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexShrink: 0 }}>
          <div style={{ fontSize: FontSizes.trading_sm, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA2.inkFaint }}>
            在線同事
          </div>
          <div style={{ fontSize: FontSizes.trading_sm, color: VA2.inkFaint, fontFamily: VA2.mono }}>
            {roster === null ? (
              <span style={{ color: VA2.inkFaint }}>…</span>
            ) : (
              <>
                <span style={{ color: VA2.up, fontWeight: 600 }}>
                  {roster.filter(p => p.has_punch_today).length}
                </span>/{roster.length}
              </>
            )}
          </div>
        </div>
        {/* va2-thin-scroll：極細捲軸（同側欄），取代原 8px 的 v2-scroll */}
        <div className="va2-thin-scroll" style={{ flex: 1, overflowY: 'auto', margin: '0 -4px' }}>
          {roster === null ? (
            <div style={{ padding: '12px 4px', fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic' }}>
              載入中…
            </div>
          ) : roster.length === 0 ? (
            <div style={{ padding: '12px 4px', fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontStyle: 'italic' }}>
              無同事資料
            </div>
          ) : (
            [...roster]
              .sort((a, b) => (b.has_punch_today ? 1 : 0) - (a.has_punch_today ? 1 : 0) || a.id - b.id)
              .map((p) => {
                const palette = ['#C3D6CC', '#E4D5C6', '#D4C8DB', '#DBCEC8', '#C8D0DB', '#D6CEB9'];
                let h = 0;
                for (let i = 0; i < (p.name || '').length; i++) h = ((h << 5) - h) + p.name.charCodeAt(i);
                const av = palette[Math.abs(h) % palette.length];
                const initials = (p.name || '?').slice(0, 1).toUpperCase();
                return (
                  <a key={p.id}
                    href={`#/employees?q=${encodeURIComponent(p.name)}`}
                    title={`查看 ${p.name} 的員工資料`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9,
                      padding: '5px 4px', borderRadius: 4,
                      cursor: 'pointer', textDecoration: 'none', color: 'inherit',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fff'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: av, color: VA2.ink,
                        display: 'grid', placeItems: 'center', fontSize: FontSizes.trading_xxs, fontWeight: 600,
                      }}>{initials}</div>
                      <span style={{
                        position: 'absolute', bottom: -1, right: -1,
                        width: 8, height: 8, borderRadius: '50%',
                        background: p.has_punch_today ? VA2.up : '#D4A04F',
                        border: `1.5px solid ${VA2.bg}`,
                      }}/>
                    </div>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6 }}>
                      <span style={{ fontSize: FontSizes.xxs, color: VA2.ink, fontWeight: 450, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                      <span style={{ fontSize: FontSizes.trading_sm, color: VA2.inkFaint, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 60 }}>
                        {p.position || p.department_name || '—'}
                      </span>
                    </div>
                  </a>
                );
              })
          )}
        </div>
      </div>


    </aside>
  );
}

/* AppSwitcher — top-right dropdown (Google 9-grid style) for cross-system navigation.
 * Lives in the trading-page header so users aren't trapped after sidebar removal. */
function AppSwitcher() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  // 每項掛 route key（對照 hr-shell.jsx ROUTES：attendance/leave/expense；
  // EXTRA_ROUTES：lunch/courses/research/files），逐一過 canAccessRoute 過濾——
  // 受限帳號（analyst / non_staff，無 privileged role）只看得到白名單內的模組，
  // 不再出現點了被 router 彈回落地頁的死連結。
  const APPS = [
    { name: '出勤',         icon: 'clock',       href: '#/attendance',  route: 'attendance', accent: true },
    { name: '請假',         icon: 'calendar',    href: '#/leave',       route: 'leave' },
    { name: '報帳',         icon: 'dollar-sign', href: '#/expense',     route: 'expense' },
    { name: '午餐',         icon: 'coffee',      href: '#/lunch',       route: 'lunch' },
    { name: '教學',         icon: 'book',        href: '#/courses',     route: 'courses' },
    { name: 'AI 研究',      icon: 'search',      href: '#/research',    route: 'research' },
    { name: '檔案庫',       icon: 'folder',      href: '#/files',       route: 'files' },
    { name: '分析工作台',   icon: 'layout',      href: '#/hub',         route: 'hub' },
  ];
  const apps = (typeof window.canAccessRoute === 'function')
    ? APPS.filter(a => window.canAccessRoute(window._USER, a.route))
    : APPS;
  // 全部被過濾掉 → 連 3x3 按鈕都不渲染（空選單沒有意義）。
  if (!apps.length) return null;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="跳到其他模組"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 6, color: '#4A5048', display: 'inline-flex', alignItems: 'center',
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = '#0E1411'}
        onMouseLeave={(e) => e.currentTarget.style.color = '#4A5048'}
      >
        {/* 3x3 dot grid icon — universal "app switcher" affordance */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="3" cy="3" r="1.4"/><circle cx="8" cy="3" r="1.4"/><circle cx="13" cy="3" r="1.4"/>
          <circle cx="3" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/><circle cx="13" cy="8" r="1.4"/>
          <circle cx="3" cy="13" r="1.4"/><circle cx="8" cy="13" r="1.4"/><circle cx="13" cy="13" r="1.4"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8, zIndex: 50,
          background: uiPick('#fff', GxT.color.card), border: '1px solid ' + uiPick('#E5E2DA', GxT.color.olv), borderRadius: 6,
          boxShadow: uiPick('0 8px 28px rgba(0,0,0,0.12)', GxT.shadow.menu), padding: 10,
          width: 268,
        }}>
          <div style={{
            fontSize: FontSizes.trading_md, color: uiPick('#8B8F89', GxT.color.mute), letterSpacing: '0.12em', textTransform: 'uppercase',
            padding: '4px 6px 8px', fontWeight: 600,
          }}>跳到模組</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
            {apps.map((a, i) => (
              <a key={a.route || i} href={a.href}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '10px 4px', borderRadius: 4, textDecoration: 'none',
                  color: a.accent ? uiPick('#0B5F5A', GxT.color.p) : uiPick('#4A5048', GxT.color.onV), fontSize: FontSizes.trading_md,
                  background: a.accent ? uiPick('#E6EEED', GxT.color.pc) : 'transparent',
                  transition: 'background 120ms',
                }}
                onMouseEnter={(e) => { if (!a.accent) e.currentTarget.style.background = uiPick('#FAF8F3', GxT.color.s1); }}
                onMouseLeave={(e) => { if (!a.accent) e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon name={a.icon} size={14} />
                <span style={{ fontWeight: a.accent ? 500 : 400 }}>{a.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────  TradingSidebar  ───────────────────
 * Reusable sidebar shared between:
 *   - VariantA2 (trading dashboard, activeRoute='dashboard')
 *   - Workspace tools rendered inside index.html (lunch/courses/research/files)
 * Pass `activeRoute` to highlight the current item. Non-ERP routes live here;
 * ERP routes live in hr.html's HR sidebar.
 */
function TradingSidebar({ activeRoute = 'dashboard' } = {}) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [changePwOpen, setChangePwOpen] = React.useState(false);
  const sidebarToast = typeof useToast !== 'undefined' ? useToast() : null;
  const currentUser = window._USER;
  // 目前停在哪個類別選擇頁（#/cat/<key>）— _router 的 route 在 #/cat/daily →
  // #/cat/quant 間都是 'cat'（同值不重渲），所以側欄自己追 hash 第二段。
  const catHub = window.CategoryHub;
  const [catHash, setCatHash] = React.useState(() => (catHub ? catHub.currentKey() : null));

  React.useEffect(() => {
    const toggle = () => setMobileOpen(o => !o);
    const close = () => setMobileOpen(false);
    window.addEventListener('toggle-sidebar', toggle);
    window.addEventListener('close-sidebar', close);
    const onHashChange = () => {
      setMobileOpen(false);
      if (window.CategoryHub) setCatHash(window.CategoryHub.currentKey());
    };
    window.addEventListener('hashchange', onHashChange);

    return () => {
      window.removeEventListener('toggle-sidebar', toggle);
      window.removeEventListener('close-sidebar', close);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  /* NavItem：route 給站內路由（active 依 activeRoute 比對）；類別大主題改傳
   * href + active（自行判定）+ count（該使用者看得到的功能數，mono 小字）。 */
  function NavItem({ route, name, icon, href, target, ext, badge, count, active, dense = false, onClick }) {
    const isActive = active !== undefined ? active : (route && activeRoute === route);
    const linkHref = route
      ? (route === 'dashboard' ? '#/' : `#/${route}`)
      : href;
    return (
      <a
        href={linkHref}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        onClick={onClick}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: dense ? '7px 12px' : '9px 12px',
          borderRadius: 8, fontSize: dense ? 12.5 : 13.5,
          color: isActive ? '#fff' : VA2.inkSoft,
          background: isActive ? VA2.accent : 'transparent',
          fontWeight: isActive ? 600 : 400, marginBottom: 2,
          textDecoration: 'none', position: 'relative',
          boxShadow: isActive ? '0 4px 12px rgba(11, 95, 90, 0.2)' : 'none',
          transition: 'all 120ms',
        }}
      >
        <Icon name={icon} size={dense ? 13 : 15} />
        <span style={{ flex: 1 }}>{name}</span>
        {ext && <Icon name="ext" size={11} stroke={2} />}
        {count !== undefined && (
          <span style={{
            fontFamily: VA2.mono, fontSize: FontSizes.trading_md,
            color: isActive ? 'rgba(255,255,255,0.7)' : VA2.inkFaint,
          }}>{count}</span>
        )}
        {badge}
      </a>
    );
  }

  // 受限帳號（analyst / non_staff，無 privileged role）：側欄只保留白名單內的路由。
  //   多角色混搭（analyst+non_staff）→ 白名單聯集（對齊後端）。
  // 側欄結構自 2026-09-14 起改為「Primary（交易紀錄／分析工作台）+ 功能分區五個大主題」，
  // 另在側欄底部（身分區上方）有常用直達純圖示列（見下方 quickItems）；各主題連到類別選擇頁 #/cat/<key>（hr-category-hub.jsx Bento
  // Grid），子項目不再在側欄展開。每個大主題的可見性 = 該分區至少一張卡片
  // 看得到（卡片可見性沿用 canAccessRoute / canAccessToolLinks / isRestrictedAccount，
  // 判定集中在 CategoryHub.visibleItems，側欄只讀結果）。
  const canRoute = (key) => (window.canAccessRoute ? window.canAccessRoute(window._USER, key) : true);
  const categories = catHub
    ? catHub.CATEGORIES
        .map(c => ({ ...c, count: catHub.visibleItems(window._USER, c.key).length }))
        .filter(c => c.count > 0)
    : [];
  // 停在某類別底下任一功能頁（如 #/payroll → ERP 系統）或該類別選擇頁本身時 highlight
  const activeCat = activeRoute === 'cat'
    ? catHash
    : (catHub ? catHub.categoryOfRoute(activeRoute) : null);
  // 「常用」直達列（側欄底部）：清單與資料都來自 CategoryHub（QUICK_ACCESS_KEYS / findItem），
  // 側欄不重寫名稱／hash／icon；可見性走 itemVisible（與類別頁卡片同一套判定，
  // ERP 項自然含 !isRestrictedAccount）。三項全看不到 → 整列隱藏。
  const quickItems = (catHub && catHub.QUICK_ACCESS_KEYS && catHub.findItem && catHub.itemVisible)
    ? catHub.QUICK_ACCESS_KEYS
        .map(k => catHub.findItem(k))
        .filter(hit => hit && catHub.itemVisible(window._USER, hit.item, hit.cat))
        .map(hit => hit.item)
    : [];

  return (
    <>
      {mobileOpen && (
        <div
          className="va2-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(14,20,17,0.45)',
            zIndex: 999,
          }}
        />
      )}
      <aside className={mobileOpen ? 'mobile-open' : ''} style={{
        background: '#F4F1E9', borderRight: `1px solid ${VA2.rule}`,
        padding: '28px 12px 20px', display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, alignSelf: 'start', height: '100vh',
      }}>
      <div style={{ padding: '0 12px 28px', borderBottom: `1px solid ${VA2.rule}`, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 6,
            background: VA2.accent, color: '#fff',
            display: 'grid', placeItems: 'center',
            fontFamily: VA2.serif, fontWeight: 600, fontSize: FontSizes.base, letterSpacing: '-0.02em',
          }}>G</div>
          <div>
            <div style={{ fontFamily: VA2.serif, fontSize: FontSizes.base, fontWeight: 500, letterSpacing: '-0.01em', lineHeight: 1.1 }}>Genesis</div>
            <div style={{ fontSize: FontSizes.trading_md, color: VA2.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>
              International Capital
            </div>
          </div>
        </div>
      </div>

      {/* va2-thin-scroll：極細捲軸（5px、平時隱形 hover 浮現），樣式在 index.html 全域 CSS，與右欄在線同事共用 */}
      <nav className="va2-thin-scroll" style={{ flex: 1, padding: '0 12px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        {/* ── 側欄只留大主題（2026-09-14）：Primary + 功能分區（常用直達列在 nav 之外、側欄底部）──
          * Primary（無標題）：交易紀錄 → 分析工作台，直達頁面。hub 是常駐入口非
          * 強制落地（員工開 #/ 仍落交易紀錄；analyst-only 無 dashboard 白名單＝只剩 hub）。
          * 功能分區：每日情報 / 個股與量化 / 深度研究 / 工具 / ERP 系統，各連到
          * 類別選擇頁 #/cat/<key>（hr-category-hub.jsx）。右側 mono 小字 = 該使用者
          * 看得到的功能數；一張卡都看不到的分區整個隱藏（與舊側欄「整區隱藏」語意一致）。
          * 外開新分頁 / NEW 標籤 / 員工限定等標示改在卡片上呈現。 */}
        <div style={{ marginBottom: 20 }}>
          {canRoute("dashboard") && <NavItem route="dashboard" name="交易紀錄" icon="file-text" />}
          {canRoute("hub") && <NavItem route="hub" name="分析工作台" icon="layout" />}
          <NavItem route="updates" name="更新消息" icon="bell" />
        </div>

        {categories.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: FontSizes.trading_sm, letterSpacing: "0.14em", textTransform: "uppercase", color: VA2.inkFaint, padding: "0 12px 8px" }}>
            功能分區
          </div>
          {categories.map(c => (
            <NavItem
              key={c.key}
              name={c.name} icon={c.icon}
              href={`#/cat/${c.key}`}
              active={activeCat === c.key}
              count={c.count}
            />
          ))}
        </div>
        )}
      </nav>

      {/* ── 常用直達純圖示列（2026-09-15）：固定幾個高頻功能頁一步直達（出勤／假勤申請／午餐訂購），
        * 清單在 hr-category-hub.jsx QUICK_ACCESS_KEYS。放在 nav 捲動區之下、身分 widget 之上，
        * 刻意不放進 .va2-thin-scroll（overflow:auto 會裁掉上浮的 tooltip）。純圖示無文字，名稱靠
        * CSS tooltip（data-tip → ::after，hover / focus-visible 顯示）＋ aria-label 給讀屏。
        * CSS 在 index.html `.va2-quick-*`。連結行為同 NavItem：純 hash href，不 preventDefault；
        * 手機抽屉靠上方 hashchange listener 收合。停在 #/attendance 時本列「出勤」與上方「ERP 系統」
        * 同時 active（後者擁有該路由）——兩個都亮是預期行為。列自帶 border-bottom 當分隔線，
        * 下方身分 widget 的 borderTop 在有本列時拿掉，避免兩條線疊在一起。 */}
      {quickItems.length > 0 && (
        <nav className="va2-quick" aria-label="常用">
          {quickItems.map(it => {
            const isActive = activeRoute === it.key;
            return (
              <a
                key={it.key}
                href={it.key === 'dashboard' ? '#/' : `#/${it.key}`}
                className={'va2-quick-item' + (isActive ? ' is-active' : '')}
                aria-current={isActive ? 'page' : undefined}
                aria-label={it.n}
                data-tip={it.n}
              >
                <Icon name={it.icon} size={17} />
              </a>
            );
          })}
        </nav>
      )}

      {/* User Identity Widget at the bottom of sidebar */}
      {currentUser && (
        <div style={{ 
          padding: '12px 12px', 
          borderTop: quickItems.length > 0 ? 'none' : `1px solid ${VA2.rule}`, 
          marginTop: 'auto', // push to bottom below nav
          flexShrink: 0 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {currentUser.avatar_url ? (
              <img src={currentUser.avatar_url} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: '#D6CEB9', color: VA2.ink,
                display: 'grid', placeItems: 'center', fontSize: FontSizes.xxs, fontWeight: 600,
              }}>{(currentUser.employee_name || currentUser.display_name || currentUser.username || '?').charAt(0).toUpperCase()}</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: FontSizes.trading_md, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentUser.employee_name || currentUser.display_name || (currentUser.username || '').split('@')[0]}
              </div>
              <div style={{ fontSize: FontSizes.trading_md, color: VA2.inkFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(currentUser.username || '').split('@')[0]}
                </span>
                {(() => {
                  // 受限帳號身分徽章：外部分析 · 進階（analyst）/ 外部分析 · 一般
                  // （analyst_lite）/ 非員工（non_staff-only）；非受限帳號維持原文字。
                  const restrictedUser = window.isRestrictedAccount && window.isRestrictedAccount(currentUser);
                  if (restrictedUser) {
                    const roles = window.userRoles ? window.userRoles(currentUser) : (currentUser.roles || []);
                    const label = roles.includes('analyst') ? '外部分析 · 進階'
                      : roles.includes('analyst_lite') ? '外部分析 · 一般'
                      : '非員工';
                    return (
                      <span style={{
                        background: VA2.accentSoft, color: VA2.accent,
                        borderRadius: 99, padding: '1px 8px', flexShrink: 0,
                        fontSize: FontSizes.trading_sm, fontWeight: 600, letterSpacing: '0.04em',
                      }}>{label}</span>
                    );
                  }
                  return <span style={{ flexShrink: 0 }}>· {(currentUser.role === 'admin' || (currentUser.roles || []).includes('admin')) ? 'Admin' : 'User'}</span>;
                })()}
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setChangePwOpen(true); }}
              title="修改密碼"
              style={{ color: VA2.inkFaint, display: 'inline-flex', marginRight: 8,
                       background: 'transparent', border: 'none', cursor: 'pointer', padding: 2 }}>
              <Icon name="key" size={13} />
            </button>
            <a onClick={(e) => { e.preventDefault(); localStorage.removeItem('token'); location.replace('/static/login.html'); }} href="#" title="登出" style={{ color: VA2.inkFaint, display: 'inline-flex' }}>
              <Icon name="log-out" size={13} />
            </a>
          </div>
          {/* UI 外殼切換（SPEC §1.2「UI 外殼選擇」）：只在 expressive 模組有載入時顯示；
            * 純 UI 偏好、所有角色皆可見。寫入 localStorage 失敗 → toast、不 reload。 */}
          {window.GxModel && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const ok = window.GxModel.switchUiShell('expressive');
                if (!ok && sidebarToast) sidebarToast('瀏覽器不允許儲存偏好', 'error');
              }}
              title="切換到新版介面（Beta）"
              style={{ color: VA2.inkFaint, background: 'transparent', border: 'none', cursor: 'pointer',
                       padding: '2px 0', marginTop: 6, marginLeft: 40, fontSize: FontSizes.trading_sm,
                       fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}>
              試用新版（Beta）
            </button>
          )}
        </div>
      )}


      {changePwOpen && ReactDOM.createPortal(
        <TradingChangePasswordModal onClose={() => setChangePwOpen(false)} />,
        document.body
      )}
    </aside></>
  );
}

/* 輕量「修改密碼」Modal — 不依賴 hr-shell.jsx 的 Modal/Field 元件，純 inline styles。 */
function TradingChangePasswordModal({ onClose }) {
  const [oldPw, setOldPw] = React.useState('');
  const [newPw, setNewPw] = React.useState('');
  const [confirmPw, setConfirmPw] = React.useState('');
  const [msg, setMsg] = React.useState(null);  // { kind: 'error'|'ok', text }
  const [busy, setBusy] = React.useState(false);
  const firstInputRef = React.useRef(null);

  // 強制把 focus 拉到舊密碼欄位（autoFocus 在某些 React render timing 下會被別處搶走）
  React.useEffect(() => {
    const t = setTimeout(() => firstInputRef.current?.focus(), 50);
    // Esc 關閉
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // 鎖背景滾動
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!oldPw || !newPw || !confirmPw) { setMsg({ kind: 'error', text: '請填寫所有欄位' }); return; }
    if (newPw.length < 8) { setMsg({ kind: 'error', text: '新密碼至少 8 個字元' }); return; }
    if (newPw !== confirmPw) { setMsg({ kind: 'error', text: '兩次新密碼不一致' }); return; }
    if (oldPw === newPw) { setMsg({ kind: 'error', text: '新密碼不能與舊密碼相同' }); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + (localStorage.getItem('token') || ''),
        },
        body: JSON.stringify({ old_password: oldPw, new_password: newPw }),
      });
      if (res.status === 204) {
        setMsg({ kind: 'ok', text: '密碼已更新' });
        setTimeout(onClose, 1200);
      } else {
        const data = await res.json().catch(() => ({}));
        setMsg({ kind: 'error', text: data.detail || `HTTP ${res.status}` });
      }
    } catch (err) {
      setMsg({ kind: 'error', text: '無法連到伺服器：' + (err.message || '') });
    } finally {
      setBusy(false);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const card = {
    background: '#fff', borderRadius: 8, padding: 28, width: 380, maxWidth: '90vw',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  };
  const inputStyle = {
    width: '100%', padding: '8px 12px', fontSize: FontSizes.xs, fontFamily: 'inherit',
    border: '1px solid #d0d0d0', borderRadius: 4, boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: FontSizes.trading_lg, color: '#666', marginBottom: 4, display: 'block', letterSpacing: '0.05em', textTransform: 'uppercase' };
  const btnGhost = { padding: '8px 14px', background: '#eee', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: FontSizes.xs };
  const btnPrimary = { padding: '8px 14px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: FontSizes.xs };

  return (
    <div
      style={overlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onMouseDown={(e) => { /* 阻擋 mousedown 落到底下 sidebar */ e.stopPropagation(); }}
    >
      <form
        style={card}
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 6px', fontSize: FontSizes.lg, fontFamily: 'Crimson Pro, serif' }}>修改密碼</h3>
        <p style={{ margin: '0 0 18px', color: '#888', fontSize: FontSizes.xxs }}>登入後自助修改 — 需先驗證舊密碼</p>

        {msg && (
          <div style={{
            padding: 10, marginBottom: 12, borderRadius: 4, fontSize: FontSizes.trading_md,
            background: msg.kind === 'ok' ? '#E8F5E9' : '#FCE8E8',
            color: msg.kind === 'ok' ? '#1B5E20' : '#B71C1C',
          }}>{msg.text}</div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>舊密碼</label>
          <input ref={firstInputRef} type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} style={inputStyle} autoComplete="current-password" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>新密碼（至少 8 字元）</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} style={inputStyle} autoComplete="new-password" />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>確認新密碼</label>
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} style={inputStyle} autoComplete="new-password" />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={busy} style={btnGhost}>取消</button>
          <button type="submit" disabled={busy} style={btnPrimary}>{busy ? '更新中…' : '確認更新'}</button>
        </div>
      </form>
    </div>
  );
}

/* embedded（Expressive 外殼 GxTradePage 用，plan §2.2）：外層改 'va2-embedded' + display:block、
 * 不渲染含 .va2-shell grid media rule 的 <style>、不渲染 WorkspaceHeader、<main> 改 <div>
 * （避免巢狀 main landmark 與 index.html `main > div` 全域內距）、內距 0。
 * embedded=false（Bento）時輸出與原本完全相同。 */
function VariantA2({ hideSidebar = false, embedded = false } = {}) {
  const MainTag = embedded ? 'div' : 'main';
  // 期間 (7D / 30D / ALL)
  const [period, setPeriod] = React.useState('ALL');    // period 只影響上方 KPI/chart，不影響表格

  // SSE 'positions:updated' → 遞增 posVersion → 觸發下面所有 useMemo / useEffect 重算
  // （取代 hr-trading.jsx 的 key={version} remount — 改成 in-place re-render，畫面不閃）
  const [posVersion, setPosVersion] = React.useState(0);
  React.useEffect(() => {
    const onUpdated = () => setPosVersion(v => v + 1);
    window.addEventListener('positions:updated', onUpdated);
    return () => window.removeEventListener('positions:updated', onUpdated);
  }, []);

  // 今年總報酬 (YTD)：從 KGI Margin() snapshot 算 — 多帳號 / 多幣別交給後端
  // 跟著 posVersion 一起 re-fetch，每次 SSE 都拿最新數字
  const [accountPnl, setAccountPnl] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api('GET', '/positions/account-pnl').then(d => {
      if (alive) setAccountPnl(d);
    }).catch(() => { /* 沒有 snapshot 就 fallback 用舊估算值 */ });
    return () => { alive = false; };
  }, [posVersion]);

  // 每日「權益相對原始資金」報酬率序列（給「交易員累計損益」折線圖）
  // returnPct = (當日權益 − 年初基準)/年初基準；線尾 = 上方大數字
  const [equityDaily, setEquityDaily] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api('GET', '/positions/equity-daily?days=30').then(d => {
      if (alive) setEquityDaily(d);
    }).catch(() => {});
    return () => { alive = false; };
  }, [posVersion]);

  // 帳戶 Tab 全員開放：isAdmin 不再 gate 帳戶顯示/切換（保留變數供未來其他用途）
  const isAdmin = window._USER && (window._USER.role === 'admin' || (window._USER.roles || []).includes('admin'));
  const [accountTab, setAccountTab] = React.useState('combined'); // 'combined' | 'trading' | 'others'

  const tabPositionsBase = React.useMemo(() => {
    if (accountTab === 'combined') return POSITIONS;  // 綜合 = 兩帳號全部
    return POSITIONS.filter(p => {
      return accountTab === 'trading' ? p.trader === '5571391' : p.trader === '5571155';
    });
  }, [accountTab, posVersion]);

  // 各標的「最後一筆取得的市價」：同 symbol 的 KGI 同步列共用 MPrice，
  // 收成 map 讓沒帶市價的列（手動列/已平倉列）也能顯示該標的最新市價。
  const symbolMarket = React.useMemo(() => {
    const m = {};
    for (const p of POSITIONS) {
      if (p.marketPrice != null) m[p.sym] = p.marketPrice;
    }
    return m;
  }, [posVersion]);
  const _lastMarket = (p) => (p.marketPrice != null ? p.marketPrice : symbolMarket[p.sym]);

  const activeAccountPnl = React.useMemo(() => {
    if (!accountPnl) return null;
    // 選「綜合」→ 用兩帳號合計 total
    if (accountTab === 'combined') return accountPnl.total;

    // 交易部 = 5571391、其他部 = 5571155
    const acct = accountTab === 'trading' ? '5571391' : '5571155';

    // 優先用後端 byAccount 的 per-account 報酬率：
    //   returnPct = (該帳號最新 equity − 該帳號年初基準) / 該帳號年初基準 × 100
    const a = (accountPnl.byAccount || {})[acct];
    if (a) {
      return {
        equity: a.equity,
        osprLos: a.osprLos,
        prtLos: a.prtLos,
        tprtLos: a.tprtLos,
        cumulativePnlTwd: a.cumulativePnlTwd,
        baselineEquity: a.baselineEquity,
        returnPct: a.returnPct
      };
    }

    // Fallback（舊後端沒回 byAccount）：用 OSPRTLOS+PRTLOS 本日近似回推。
    const accountsOut = accountPnl.accounts || [];
    const selectForSum = (account) => {
      const rowsForAcct = accountsOut.filter(x => x.account === account);
      if (!rowsForAcct.length) return [];
      const starred = rowsForAcct.filter(x => x.currency === '**');
      if (starred.length) return starred;
      const twd = rowsForAcct.filter(x => x.currency === 'TWD');
      if (twd.length) return twd;
      return rowsForAcct;
    };

    let sumEquity = 0, sumOspr = 0, sumPrt = 0, sumTprt = 0;
    for (const r of selectForSum(acct)) {
      sumEquity += (r.equity || 0);
      sumOspr += (r.osprLos || 0);
      sumPrt += (r.prtLos || 0);
      sumTprt += (r.tprtLos || 0);
    }

    const ytdPnl = sumOspr + sumPrt;
    const baselineEquity = sumEquity - ytdPnl;
    const returnPct = baselineEquity ? (ytdPnl / baselineEquity * 100) : 0;

    return {
      equity: sumEquity,
      osprLos: sumOspr,
      prtLos: sumPrt,
      tprtLos: sumTprt,
      cumulativePnlTwd: ytdPnl,
      baselineEquity: baselineEquity,
      returnPct: returnPct
    };
  }, [accountPnl, accountTab]);

  const periodPositions = React.useMemo(() => {
    if (period === 'ALL') return tabPositionsBase;
    if (period === 'TODAY') {
      const today = new Date().toISOString().slice(0, 10);  // YYYY-MM-DD (UTC; 對 TPE 通常夠用)
      const todayTpe = new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
      return tabPositionsBase.filter(p => {
        // 優先 p.ot（已 strip 00:00:00），fallback createdAt
        const dateStr = String(p.ot || p.createdAt || '').slice(0, 10);
        if (!dateStr) return false;
        return dateStr === today || dateStr === todayTpe;
      });
    }
    const days = period === '7D' ? 7 : 30;
    const cutoff = Date.now() - days * 86400 * 1000;
    return tabPositionsBase.filter(p => {
      if (!p.createdAt) return true;  // 沒時間戳當作有效
      const t = Date.parse(String(p.createdAt).replace(' ', 'T') + (String(p.createdAt).includes('Z') ? '' : 'Z'));
      return Number.isFinite(t) ? t >= cutoff : true;
    });
  }, [period, tabPositionsBase]);

  // Headline 數字
  // 主要顯示「報酬率 %」(returnPct = pnl / (equity - pnl))。
  // totalPnl 仍計算著（供 sign/color 判斷），但 UI 不顯示絕對金額。
  const legacyOpenPnl = periodPositions.reduce((s, p) => s + (p.pnl || 0), 0);
  const ytdPnl = activeAccountPnl?.cumulativePnlTwd;
  const totalPnl = (typeof ytdPnl === 'number') ? ytdPnl : legacyOpenPnl;
  const ytdReturnPct = activeAccountPnl?.returnPct;  // 主顯示用
  const totalLots = periodPositions.reduce((s, p) => s + (p.lots || 0), 0);
  const totalTrades = periodPositions.length;
  const longLots = periodPositions.filter(p => p.side === 'B').reduce((s, p) => s + (p.lots || 0), 0);
  const shortLots = periodPositions.filter(p => p.side === 'S').reduce((s, p) => s + (p.lots || 0), 0);
  const longShortRatio = shortLots > 0 ? (longLots / shortLots).toFixed(1) : '∞';
  const holdingCnt = periodPositions.filter(p => p.st === 'holding').length;
  const closedCnt = periodPositions.filter(p => p.st === 'closed').length;
  const winCnt = periodPositions.filter(p => p.pnl > 0).length;
  const avgWin = periodPositions.length ? winCnt / periodPositions.length : (TRADERS.length ? TRADERS.reduce((s, t) => s + t.win, 0) / TRADERS.length : 0);
  const traderCnt = new Set(periodPositions.map(p => p.trader).filter(Boolean)).size;

  // WoW%: 比較 period 與前一個相同長度的 window
  const wowPct = React.useMemo(() => {
    if (period === 'ALL') return null;
    const days = period === '7D' ? 7 : 30;
    const now = Date.now();
    const cur = tabPositionsBase.filter(p => {
      const t = p.createdAt ? Date.parse(String(p.createdAt).replace(' ', 'T') + 'Z') : NaN;
      return Number.isFinite(t) && t >= now - days * 86400 * 1000;
    }).reduce((s, p) => s + (p.pnl || 0), 0);
    const prev = tabPositionsBase.filter(p => {
      const t = p.createdAt ? Date.parse(String(p.createdAt).replace(' ', 'T') + 'Z') : NaN;
      return Number.isFinite(t) && t < now - days * 86400 * 1000 && t >= now - 2 * days * 86400 * 1000;
    }).reduce((s, p) => s + (p.pnl || 0), 0);
    if (!prev) return null;
    return { pct: ((cur - prev) / Math.abs(prev)) * 100, cur, prev };
  }, [period, tabPositionsBase]);

  // 表格互動狀態
  const [tab, setTab] = React.useState('holding');       // all | holding | closed — 預設「留倉」
  const [sortBy, setSortBy] = React.useState('orderTime');  // orderTime | pnl | lots
  const [sortDir, setSortDir] = React.useState('desc');
  const [sortMenuOpen, setSortMenuOpen] = React.useState(false);
  // 點 row 開詳情 modal — 因為 aggregated 行 fills 太多 / note 太長表格塞不下
  const [detailRow, setDetailRow] = React.useState(null);

  // 交易緣由編輯（任何登入者可填；後端 PATCH /positions/{id}/remark）
  // noteEdit === null 表示非編輯狀態；aggregated 行會把同組每個 position id 都更新
  const [noteEdit, setNoteEdit] = React.useState(null);
  const [noteSaving, setNoteSaving] = React.useState(false);
  const _detailEditableIds = (row) =>
    (Array.isArray(row?.ids) ? row.ids : [row?.id]).filter(x => Number.isInteger(x));
  async function saveDetailNote() {
    const ids = _detailEditableIds(detailRow);
    if (!ids.length) return;
    setNoteSaving(true);
    try {
      for (const id of ids) {
        const r = await fetch('/api/positions/' + id + '/remark', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('token'),
          },
          body: JSON.stringify({ remark: noteEdit }),
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || ('HTTP ' + r.status));
        }
      }
      location.reload();
    } catch (e) {
      alert('交易緣由儲存失敗：' + e.message);
      setNoteSaving(false);
    }
  }

  // 今日日期 (本地時區) — 給「NEW」tag 判斷用
  const _todayDates = React.useMemo(() => {
    const u = new Date().toISOString().slice(0, 10);
    const t = new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
    return new Set([u, t]);
  }, [posVersion]);
  const _isNewToday = (p) => _todayDates.has(String(p.ot || p.createdAt || '').slice(0, 10));

  // 成交時間：聚合列（partial fills）只顯示單一時間點（第一筆成交），
  // 不顯示「10:40 ~ 10:45」區間；各筆完整時間在詳情 Modal 的成交明細裡。
  const _execTimeDisplay = (p) => String(p.earliestOt || p.ot || '').replace(/\s+00:00:00$/, '');

  const filteredPositions = React.useMemo(() => {
    // 表格永遠顯示全部（按 tab / trader filter），不受 period 影響
    let out = tabPositionsBase;
    if (tab !== 'all') out = out.filter(p => p.st === tab);
    const dir = sortDir === 'asc' ? 1 : -1;
    // 排序：① 今日新紀錄優先 ② trader asc ③ user 選的 sortBy
    out = [...out].sort((a, b) => {
      const newDiff = (_isNewToday(b) ? 1 : 0) - (_isNewToday(a) ? 1 : 0);
      if (newDiff !== 0) return newDiff;
      const t = String(a.trader || '').localeCompare(String(b.trader || ''));
      if (t !== 0) return t;
      if (sortBy === 'pnl') return (a.pnl - b.pnl) * dir;
      if (sortBy === 'lots') return (a.lots - b.lots) * dir;
      return (String(a.ot).localeCompare(String(b.ot))) * dir;
    });
    return out;
  }, [tabPositionsBase, tab, sortBy, sortDir, posVersion, _todayDates]);

  // 權限：admin 或白名單交易員可新增/刪除
  const TRADE_EDITORS = ['gsinvest009', 'gsinvest008'];
  const userLocalPart = ((window._USER && window._USER.username) || '').split('@')[0];
  const canEdit = !!window._USER && (window._USER.role === 'admin' || (window._USER.roles || []).includes('admin') || TRADE_EDITORS.includes(userLocalPart));

  // 新增交易 modal 狀態
  const [modalOpen, setModalOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [formMsg, setFormMsg] = React.useState('');
  const emptyForm = { productName: '', side: 'buy', lots: 1, entryPrice: 0, trader: userLocalPart || '', orderTime: '', remark: '', status: 'holding' };
  const [form, setForm] = React.useState(emptyForm);

  async function submitNewPosition() {
    if (!form.productName.trim()) { setFormMsg('請輸入標的'); return; }
    setSubmitting(true);
    setFormMsg('');
    try {
      const r = await fetch('/api/positions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
        },
        body: JSON.stringify({
          product_name: form.productName.trim(),
          side: form.side,
          lots: Number(form.lots) || 1,
          entry_price: Number(form.entryPrice) || 0,
          currency: 'TWD',
          trader: form.trader.trim(),
          order_time: form.orderTime.trim(),
          status: form.status,
          remark: form.remark.trim(),
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || ('HTTP ' + r.status));
      }
      setForm(emptyForm);
      setModalOpen(false);
      location.reload();
    } catch (e) {
      setFormMsg('新增失敗：' + e.message);
    } finally {
      setSubmitting(false);
    }
  }

  // 刪除狀態
  // 刪除鈕只開放「非綁定帳號」的列：本專案綁定兩個真實期貨帳號（交易部 5571391、
  // 其他部 5571155），trader 屬於它們 = 真實 KGI 留倉，受保護不可從 UI 刪；
  // 其餘（mock 假資料 trader='KGI'、手動輸入列 trader=人名等）才顯示刪除鈕。
  const KGI_BOUND_ACCOUNTS = ['5571391', '5571155'];
  const _isProtectedRow = (p) => KGI_BOUND_ACCOUNTS.includes(String(p.trader || ''));
  // 可刪 = 非綁定帳號列且有 positions DB id（平倉歷史列的 id 是合成字串，來自 trade_history，
  // 本端點刪不到）。聚合列（partial fills）把整組 ids 一起刪。
  const _deletableIds = (p) => _isProtectedRow(p) ? [] : Array.from(new Set(
    (Array.isArray(p.ids) && p.ids.length ? p.ids : [p.id]).filter(x => typeof x === 'number')
  ));
  const [deletingId, setDeletingId] = React.useState(null);
  async function deletePosition(p, label) {
    const ids = _deletableIds(p);
    if (!ids.length) return;
    const what = ids.length > 1 ? '「' + label + '」的 ' + ids.length + ' 筆紀錄' : '「' + label + '」';
    if (!confirm('確定刪除 ' + what + '？（非綁定帳號紀錄，軟刪除並寫入稽核）')) return;
    setDeletingId(p.id);
    try {
      for (const id of ids) {
        const r = await fetch('/api/positions/' + id, {
          method: 'DELETE',
          headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        });
        if (!r.ok) throw new Error('HTTP ' + r.status);
      }
      location.reload();
    } catch (e) {
      alert('刪除失敗：' + e.message);
      setDeletingId(null);
    }
  }

  return (
    <div className={embedded ? 'va2-embedded' : 'va2-shell va2-shell-rwd'} style={{
      /* Three-column shell matching ERP pattern:
       *   sidebar 244 · main 1fr · right rail 360
       * Rail hides below 1480px viewport via the media query in <style> below. */
      width: '100%',
      minHeight: hideSidebar ? 'auto' : '100vh',
      background: VA2.bg, color: VA2.ink,
      fontFamily: VA2.sansDisplay,
      fontFeatureSettings: '"ss01","cv11","tnum"',
      display: embedded ? 'block' : 'grid',
      gridTemplateColumns: hideSidebar ? '1fr' : '244px minmax(0, 1fr) 360px',
    }}>
      {!embedded && <style>{`
        .v2-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .v2-scroll::-webkit-scrollbar-track { background: #F1EEE7; border-radius: 4px; }
        .v2-scroll::-webkit-scrollbar-thumb { background: #C9C4B6; border-radius: 4px; border: 2px solid #F1EEE7; }
        .v2-scroll::-webkit-scrollbar-thumb:hover { background: #A8A391; }
        .v2-scroll { scrollbar-width: thin; scrollbar-color: #C9C4B6 #F1EEE7; }
        /* Responsive: hide right rail when viewport too narrow for 3 columns */
        @media (max-width: 1479px) {
          .va2-shell { grid-template-columns: 244px minmax(0, 1fr) !important; }
          .va2-right-rail { display: none !important; }
        }
      `}</style>}
      {/* ═══ Sidebar (extracted to TradingSidebar; hidden when embedded in Workspace shell) ═══ */}
      {!hideSidebar && <TradingSidebar activeRoute="dashboard" />}

      {/* ═══ Main ═══ */}
      <MainTag className={embedded ? 'va2-embedded-main' : undefined} style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Top bar — extracted to WorkspaceHeader so PageShell can reuse it */}
        {!embedded && <WorkspaceHeader />}

        <div style={{ padding: embedded ? 0 : '36px 40px 40px', flex: 1 }}>
          {/* Masthead — ONE primary action only */}
          <div className="va2-masthead" style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            paddingBottom: 22, borderBottom: `2px solid ${VA2.ink}`, marginBottom: 28,
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.24em', textTransform: 'uppercase', color: VA2.inkFaint }}>
                  交易儀表板 · Trading Desk
                </div>
                <div style={{ width: 24, height: 1, background: VA2.inkFaint }} />
                <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontFamily: VA2.mono }}>
                  {va2DeskDateLabel()}
                </div>
              </div>
              <h1 style={{
                fontFamily: VA2.serif, fontSize: FontSizes.xl, fontWeight: 400,
                letterSpacing: '-0.02em', lineHeight: 1, margin: 0, color: VA2.ink,
              }}>
                {(() => {
                  const u = window._USER || {};
                  const name = u.employee_name || u.display_name || (u.username || '').split('@')[0] || '';
                  // Same wording as Expressive home: 「午安，<name>。」 (Taipei time)
                  return `${va2Salute()}${name ? '，' + name : ''}。`;
                })()}
              </h1>
              {/* 每日一語 — daily market quote, deterministic by date */}
              {(() => {
                const QUOTES = [
                  { q: '今天，就是你最好的一次機會。', a: 'Genesis Desk' },
                  { q: '保持冷靜，世界永遠獎賞有紀律的人。', a: 'Ray Dalio' },
                  { q: '你不需要全壘打，只要穩穩打出安打。', a: 'Warren Buffett' },
                  { q: '最大的風險，是不敢承擔任何風險。', a: 'Mark Zuckerberg' },
                  { q: '每一筆交易，都是一次變得更好的機會。', a: 'Genesis Desk' },
                  { q: '成功不是終點，失敗也不是終結，勇氣才是關鍵。', a: 'Winston Churchill' },
                  { q: '慢慢來，比較快。', a: '日本諺語' },
                  { q: '你已經比昨天的自己進步了。', a: 'Genesis Desk' },
                  { q: '當你想放棄時，想想當初為什麼開始。', a: 'Hal Elrod' },
                  { q: '市場永遠會回來，耐心的人也會。', a: 'Peter Lynch' },
                  { q: '好交易員不是沒有虧損，而是懂得止損。', a: 'Paul Tudor Jones' },
                  { q: '今天種下的紀律，是明天的自由。', a: 'Genesis Desk' },
                  { q: '深呼吸，你比你想的更強。', a: 'A.A. Milne' },
                  { q: '專注自己，別人都只是雜音。', a: 'Genesis Desk' },
                ];
                const today = new Date();
                const doy = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
                const { q, a } = QUOTES[doy % QUOTES.length];
                return (
                  <div style={{
                    marginTop: 16, display: 'flex', alignItems: 'baseline', gap: 14,
                    paddingLeft: 14, borderLeft: `2px solid ${VA2.accent}`,
                    maxWidth: 680,
                  }}>
                    <div style={{
                      fontFamily: VA2.serif, fontSize: FontSizes.base, fontStyle: 'italic',
                      color: VA2.inkSoft, lineHeight: 1.5,
                    }}>
                      「{q}」
                    </div>
                    <div style={{
                      fontSize: FontSizes.trading_lg, color: VA2.inkFaint, fontFamily: VA2.mono,
                      letterSpacing: '0.04em', whiteSpace: 'nowrap',
                    }}>
                      — {a}
                    </div>
                  </div>
                );
              })()}
            </div>
            {/* Only ONE action: 新增交易 (僅交易員與 admin 可見) */}
            {canEdit && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <button onClick={() => setModalOpen(true)} style={{
                  border: 'none', background: VA2.accent, color: '#fff',
                  padding: '11px 18px', borderRadius: 4, fontSize: FontSizes.xs, fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  letterSpacing: '0.01em', boxShadow: '0 1px 0 rgba(11,95,90,0.3)',
                  fontFamily: 'inherit',
                }}>
                  <Icon name="plus" size={14} stroke={2.5} /> 新增交易
                </button>
              </div>
            )}
          </div>

          {/* KGI Account selection tab bar — 全員可見可切換 */}
          <div style={{ display: 'flex', marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 2, background: '#F1EEE7', padding: 3, borderRadius: 5 }}>
              {[
                ['綜合', 'combined'],
                ['交易部門帳戶 (5571391)', 'trading'],
                ['其他部門帳戶 (5571155)', 'others']
              ].map(([label, key], i) => {
                const active = accountTab === key;
                return (
                  <div key={i} onClick={() => setAccountTab(key)} style={{
                    padding: '6px 14px', borderRadius: 3, fontSize: FontSizes.xxs,
                    background: active ? '#fff' : 'transparent',
                    color: active ? VA2.ink : VA2.inkSoft,
                    fontWeight: active ? 500 : 400,
                    boxShadow: active ? '0 1px 0 rgba(0,0,0,0.04)' : 'none',
                    cursor: 'pointer', userSelect: 'none',
                  }}>
                    {label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lede numbers — unchanged (it's good) */}
          <div className="va2-lede-stats" style={{
            display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1fr',
            borderBottom: `1px solid ${VA2.rule}`, paddingBottom: 28, marginBottom: 28,
          }}>
            <div style={{ paddingRight: 40, borderRight: `1px solid ${VA2.rule}` }}>
              <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.16em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 14 }}>
                今年報酬率 · YTD Return
                {accountPnl?.startDate && (
                  <span style={{ marginLeft: 8, color: VA2.inkFaint, opacity: 0.7 }}>
                    (since {accountPnl.startDate})
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18 }}>
                <div className="va2-pnl-amount" style={{
                  fontFamily: VA2.serif, fontSize: FontSizes.xxl, fontWeight: 400,
                  letterSpacing: '-0.035em', lineHeight: 0.92,
                  color: va2Mkt(totalPnl >= 0 ? VA2.up : VA2.down, totalPnl >= 0), fontVariantNumeric: 'tabular-nums',
                }}>
                  {typeof ytdReturnPct === 'number'
                    ? `${ytdReturnPct >= 0 ? '+' : '−'}${Math.abs(ytdReturnPct).toFixed(2)}%`
                    : '—'}
                </div>
                {wowPct && (
                  <div style={{ paddingBottom: 8 }}>
                    <div style={{
                      display:'inline-flex', alignItems:'center', gap: 4,
                      padding: '3px 8px', borderRadius: 3,
                      background: uiPick(wowPct.pct >= 0 ? VA2.accentSoft : '#FCE9E8',
                        wowPct.pct >= 0 ? GxT.color.mktUpC : GxT.color.mktDownC),
                      color: uiPick(wowPct.pct >= 0 ? VA2.accent : VA2.down,
                        wowPct.pct >= 0 ? GxT.color.onMktUpC : GxT.color.onMktDownC),
                      fontSize: FontSizes.trading_lg, fontWeight: 500,
                    }}>
                      <Icon name={wowPct.pct >= 0 ? 'trending-up' : 'trending-down'} size={11} stroke={2.2} />
                      {(wowPct.pct >= 0 ? '+' : '') + wowPct.pct.toFixed(1) + '% '}
                      {period === '7D' ? 'WoW' : 'MoM'}
                    </div>
                    <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, marginTop: 6, fontFamily: VA2.mono }}>
                      vs. 上期 ${Math.abs(wowPct.prev).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {[
              { k: '總筆數', v: totalTrades, sub: holdingCnt + ' 留倉 · ' + closedCnt + ' 已平', tint: VA2.ink },
              { k: '總口數', v: totalLots, sub: <>多<br/>{longShortRatio}× 空</>, tint: VA2.ink },
              { k: '平均勝率', v: fmt.pct(avgWin), sub: traderCnt + ' 位交易員', tint: VA2.up },
            ].map((x, i) => (
              <div key={i} style={{ padding: '0 28px', borderRight: i<2 ? `1px solid ${VA2.rule}`:'none' }}>
                <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.16em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 14 }}>
                  {x.k}
                </div>
                <div style={{
                  fontFamily: VA2.serif, fontSize: FontSizes.xl, fontWeight: 400,
                  letterSpacing: '-0.03em', lineHeight: 1,
                  color: x.tint, fontVariantNumeric: 'tabular-nums',
                }}>{x.v}</div>
                <div style={{ fontSize: FontSizes.xxs, color: VA2.inkFaint, marginTop: 10 }}>{x.sub}</div>
              </div>
            ))}
          </div>

          {/* Main chart + trader leaderboard — restored from v1 */}
          <div className="va2-figures" style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 28, marginBottom: 28 }}>
            <div style={{ background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6, padding: '22px 24px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 6 }}>
                    Figure 01
                  </div>
                  <h3 style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }}>
                    交易員累計損益
                  </h3>
                  <div style={{ fontSize: FontSizes.xxs, color: VA2.inkFaint, marginTop: 3 }}>帳戶權益報酬率 · 相對原始資金（含未實現，線尾＝今年報酬率）</div>
                </div>
              </div>
              {typeof EditorialChart !== 'undefined' && <EditorialChart accountTab={accountTab !== 'combined' ? accountTab : null} daily={equityDaily} />}
            </div>

            <div style={{ background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6, padding: '22px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 6 }}>
                    Figure 02
                  </div>
                  <h3 style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }}>
                    持倉分佈 <span style={{ color: VA2.inkFaint, fontStyle: 'italic' }}>— By Symbol</span>
                  </h3>
                </div>
                <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkFaint, display: 'flex', alignItems: 'center', gap: 4 }}>
                  本週 <Icon name="chevron-down" size={12} />
                </div>
              </div>
              <div className="v2-scroll" style={{ maxHeight: 260, overflowY: 'scroll', paddingRight: 6, paddingTop: 4 }}>
                {(() => {
                  // 從 tabPositionsBase 即時聚合各標的「名目金額」佔比（口數 × 成交價）。
                  // 用金額而非口數：6 口低價合約不該壓過 2 口高價合約。
                  // （持倉皆為個股期貨、合約乘數一致，故 lots × price 的比例即名目價值比例；
                  //   若未來混入大台/小台等不同乘數商品，需再乘上各自乘數。）
                  // 只算「留倉(holding)」—— 持倉分佈代表當前部位，已平倉(closed)
                  // 不該出現（否則會多出像力晶積成、瀚宇彩晶等已平標的，跟「留倉」對不起來）。
                  const byS = new Map();
                  for (const p of tabPositionsBase) {
                    if (p.st !== 'holding') continue;
                    const priceN = parseFloat(String(p.price).replace(/[,$\s]/g, '')) || 0;
                    const notional = (p.lots || 0) * priceN;
                    byS.set(p.sym, (byS.get(p.sym) || 0) + notional);
                  }
                  const total = Array.from(byS.values()).reduce((a, b) => a + b, 0) || 1;
                  const rows = Array.from(byS.entries())
                    .map(([sym, amt]) => [sym, Math.round(amt / total * 100)])
                    .sort((a, b) => b[1] - a[1]);
                  // 取 top 5，其餘合併成「其他」
                  const top = rows.slice(0, 5);
                  const rest = rows.slice(5).reduce((a, b) => a + b[1], 0);
                  const data = rest > 0 ? [...top, ['其他', rest]] : top;
                  if (!data.length) {
                    return <div style={{ textAlign: 'center', color: VA2.inkFaint, fontSize: FontSizes.xxs, padding: '32px 0' }}>尚無持倉</div>;
                  }
                  const max = Math.max(...data.map(d => d[1]));
                  return data.map(([l, v], i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 32px', gap: 10, alignItems: 'center', marginBottom: 9 }}>
                      <span style={{ fontSize: FontSizes.trading_xl, color: VA2.inkSoft, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l}</span>
                      <div style={{ height: 10, background: VA2.ruleSoft, borderRadius: 1, overflow: 'hidden' }}>
                        <div style={{ width: `${v/max*100}%`, height: '100%', background: i === 0 ? VA2.accent : `oklch(55% 0.05 ${170 - i*12})` }} />
                      </div>
                      <span style={{ fontSize: FontSizes.trading_lg, fontFamily: VA2.mono, color: VA2.inkSoft, textAlign: 'right' }}>{v}%</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Table — consolidated toolbar, hover-reveal row actions, clearer badge hierarchy */}
          <div style={{ background: VA2.surface, border: `1px solid ${VA2.rule}`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '20px 24px', borderBottom: `1px solid ${VA2.rule}`, gap: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 4 }}>
                    Ledger
                  </div>
                  <h3 style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }}>
                    交易紀錄 <span style={{ color: VA2.inkFaint, fontStyle: 'italic' }}>— 近 30 日</span>
                  </h3>
                </div>
              </div>

              {/* Consolidated toolbar: tabs + single filter button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
                {/* Tabs */}
                <div style={{ display: 'flex', gap: 2, background: '#F1EEE7', padding: 3, borderRadius: 5 }}>
                  {[
                    ['全部', tabPositionsBase.length, 'all'],
                    ['留倉', tabPositionsBase.filter(p=>p.st==='holding').length, 'holding'],
                    ['已平', tabPositionsBase.filter(p=>p.st==='closed').length, 'closed'],
                  ].map(([l, n, key], i) => {
                    const a = tab === key;
                    return (
                      <div key={i} onClick={() => setTab(key)} style={{
                        padding: '5px 11px', borderRadius: 3, fontSize: FontSizes.xxs,
                        background: a ? '#fff' : 'transparent',
                        color: a ? VA2.ink : VA2.inkSoft,
                        fontWeight: a ? 500 : 400,
                        boxShadow: a ? '0 1px 0 rgba(0,0,0,0.04)' : 'none',
                        display: 'flex', alignItems: 'center', gap: 6,
                        cursor: 'pointer', userSelect: 'none',
                      }}>
                        {l}
                        <span style={{
                          fontSize: FontSizes.trading_sm, padding: '1px 6px', borderRadius: 8,
                          background: a ? '#F1EEE7' : '#E8E4D9',
                          fontFamily: VA2.mono, color: VA2.inkSoft,
                        }}>{n}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Divider */}
                <div style={{ width: 1, height: 20, background: VA2.rule }} />

                {/* Sort */}
                <div style={{ position: 'relative' }}>
                  <div onClick={() => { setSortMenuOpen(o => !o); }} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 11px', border: `1px solid ${VA2.rule}`, borderRadius: 4,
                    fontSize: FontSizes.xxs, color: VA2.inkSoft, cursor: 'pointer', userSelect: 'none',
                  }}>
                    排序: {sortBy === 'orderTime' ? '成交時間' : sortBy === 'pnl' ? '損益' : '口數'} {sortDir === 'desc' ? '↓' : '↑'}
                    <Icon name="chevron-down" size={11} />
                  </div>
                  {sortMenuOpen && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20,
                      background: '#fff', border: `1px solid ${VA2.rule}`, borderRadius: 5,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)', minWidth: 180, padding: 4,
                    }}>
                      {[
                        ['orderTime', 'desc', '成交時間 (新→舊)'],
                        ['orderTime', 'asc',  '成交時間 (舊→新)'],
                        ['pnl', 'desc',       '損益 (高→低)'],
                        ['pnl', 'asc',        '損益 (低→高)'],
                        ['lots', 'desc',      '口數 (多→少)'],
                      ].map(([by, dir, label], i) => {
                        const a = sortBy === by && sortDir === dir;
                        return (
                          <div key={i} onClick={() => { setSortBy(by); setSortDir(dir); setSortMenuOpen(false); }} style={{
                            padding: '7px 12px', borderRadius: 3, fontSize: FontSizes.xxs, cursor: 'pointer',
                            color: a ? VA2.accent : VA2.ink,
                            background: a ? VA2.accentSoft : 'transparent',
                          }}
                          onMouseEnter={(e) => { if (!a) e.currentTarget.style.background = '#F6F4EF'; }}
                          onMouseLeave={(e) => { if (!a) e.currentTarget.style.background = 'transparent'; }}>
                            {label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="va2-table-wrapper" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: '#FAF8F3', fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA2.inkFaint }}>
                  <th style={{ padding: '10px 16px 10px 24px', textAlign: 'left', fontWeight: 500 }}>帳戶</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>標的</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 500 }}>方向</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>口數</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>成交價格</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>最後取得價格</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>成交時間</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>損益</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
                  <th style={{ padding: '10px 24px 10px 16px', textAlign: 'left', fontWeight: 500 }}>交易緣由</th>
                  <th style={{ padding: '10px 16px', width: 48 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredPositions.length === 0 && (
                  <tr><td colSpan="11" style={{ padding: '32px 24px', textAlign: 'center', color: VA2.inkFaint, fontSize: FontSizes.xs }}>
                    {tabPositionsBase.length === 0 ? '尚無交易紀錄' : '此條件下無符合資料'}
                  </td></tr>
                )}
                {filteredPositions.map((p, i) => (
                  <tr key={Array.isArray(p.ids) ? p.ids.join('-') : (p.id || i)} className="tr-hover" style={{ borderTop: `1px solid ${VA2.ruleSoft}`, cursor: 'pointer' }}
                      onClick={() => { setDetailRow(p); setNoteEdit(null); }}>
                    <td style={{ padding: '14px 16px 14px 24px' }}>
                      {/* trader 欄存的是 KGI 帳號（5571391 交易部 / 5571155 其他部），非人名 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          background: p.trader === '5571391' ? VA2.accent : '#A56A1F',
                        }}/>
                        <span style={{ fontWeight: 500, fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums' }}>{p.trader}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontWeight: 500 }}>
                      {p.sym}
                      {_isNewToday(p) && (
                        <span style={{
                          marginLeft: 8, padding: '1px 6px', borderRadius: 3,
                          background: VA2.accent, color: '#fff',
                          fontSize: FontSizes.trading_xs, fontWeight: 600,
                          letterSpacing: '0.04em', verticalAlign: 'middle',
                        }}>NEW</span>
                      )}
                    </td>
                    {/* Direction — text + color only, no fill */}
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: FontSizes.xxs, fontWeight: 600, letterSpacing: '0.04em',
                        color: va2Mkt(p.side === 'B' ? VA2.accent : VA2.down, p.side === 'B'),
                      }}>{p.side === 'B' ? '多' : '空'}</span>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums' }}>{p.lots}</td>
                    {/* 成交價格：後端從 remark 自動段解析的 dealPrice（無則退回顯示字串 price） */}
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums' }}>
                      {p.dealPrice != null ? p.dealPrice.toLocaleString() : p.price}
                    </td>
                    {/* 目前價格：該標的最後一筆取得的市價（無任何來源時顯示 —） */}
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums', color: VA2.inkSoft }}>
                      {_lastMarket(p) != null ? _lastMarket(p).toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '14px 16px', color: VA2.inkSoft, fontFamily: VA2.mono, fontSize: FontSizes.xxs }}>{_execTimeDisplay(p)}</td>
                    <td style={{
                      padding: '14px 16px', textAlign: 'right',
                      fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 500,
                      color: va2Mkt(p.pnl >= 0 ? VA2.up : VA2.down, p.pnl >= 0),
                    }}>{fmt.pnl(p.pnl)}</td>
                    {/* Status — dot + text only */}
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: FontSizes.xxs, color: VA2.inkSoft }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: p.st === 'holding' ? VA2.warn : VA2.inkFaint,
                        }}/>
                        {p.st === 'holding' ? '留倉' : '已平'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', color: VA2.inkSoft, fontStyle: 'italic', fontFamily: VA2.serif, fontSize: FontSizes.xs, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.note ? `"${p.note}"` : '—'}
                    </td>
                    {/* 刪除（admin/編輯者限定；只對非綁定帳號的列顯示，見 _isProtectedRow）*/}
                    <td style={{ padding: '14px 16px 14px 8px', textAlign: 'right' }}
                        onClick={(e) => e.stopPropagation()}>
                      {canEdit && _deletableIds(p).length > 0 && (
                        <span className="row-more"
                          onClick={() => deletePosition(p, p.sym)}
                          title="刪除此紀錄（非綁定帳號）"
                          style={{
                            display: 'inline-flex', padding: 4, borderRadius: 4,
                            color: deletingId === p.id ? VA2.down : VA2.inkFaint,
                            // 常駐微顯示（0.35），hover 該列時由 .tr-hover CSS 提到 1 — 之前 hover 才浮現太難發現
                            opacity: deletingId === p.id ? 1 : 0.35, cursor: 'pointer',
                          }}>
                          <Icon name={deletingId === p.id ? 'refresh' : 'trash'} size={14} />
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <style>{`
              .tr-hover:hover { background: #FAF8F3; }
              .tr-hover:hover .row-more { opacity: 1; }
            `}</style>
          </div>

          {/* Footer */}
          <div style={{
            marginTop: 28, paddingTop: 16, borderTop: `1px solid ${VA2.rule}`,
            fontSize: FontSizes.trading_lg, color: VA2.inkFaint,
            display: 'flex', justifyContent: 'space-between',
            fontFamily: VA2.mono, letterSpacing: '0.04em',
          }}>
            <span>GENESIS INTERNATIONAL CAPITAL CORP. · TRADING DESK EDITION</span>
            <span>Delayed 15m · © 2026</span>
          </div>
        </div>

        {/* Discord — SINGLE entry point (floating tab, not top-bar + tab + FAB) */}
      </MainTag>

      {/* ═══ Right Rail — trader ambient widgets (matches ERP layout pattern) ═══ */}
      {!hideSidebar && <TraderRightRail />}

      {/* ═══ 新增交易 Modal ═══ */}
      {modalOpen && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }} style={{
          position: 'fixed', inset: 0, background: 'rgba(14,20,17,0.45)', zIndex: 100,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px',
          overflowY: 'auto',
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, width: '100%', maxWidth: 520,
            padding: '28px 32px 24px', boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 4 }}>
                  New Entry
                </div>
                <h3 style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0 }}>
                  新增交易紀錄
                </h3>
              </div>
              <button onClick={() => setModalOpen(false)} style={{ background: 'transparent', border: 'none', fontSize: FontSizes.lg, color: VA2.inkFaint, cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <label style={{ display: 'block' }}>
                <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkSoft, marginBottom: 5, fontWeight: 500 }}>標的 *</div>
                <input value={form.productName} onChange={(e) => setForm({...form, productName: e.target.value})} placeholder="例如：2330 台積電" style={{ width: '100%', padding: '9px 12px', border: `1px solid ${VA2.rule}`, borderRadius: 4, fontSize: FontSizes.xs, fontFamily: 'inherit' }} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <label>
                  <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkSoft, marginBottom: 5, fontWeight: 500 }}>方向</div>
                  <select value={form.side} onChange={(e) => setForm({...form, side: e.target.value})} style={{ width: '100%', padding: '9px 10px', border: `1px solid ${VA2.rule}`, borderRadius: 4, fontSize: FontSizes.xs, fontFamily: 'inherit', background: '#fff' }}>
                    <option value="buy">多 (Buy)</option>
                    <option value="sell">空 (Sell)</option>
                  </select>
                </label>
                <label>
                  <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkSoft, marginBottom: 5, fontWeight: 500 }}>口數</div>
                  <input type="number" min="1" value={form.lots} onChange={(e) => setForm({...form, lots: e.target.value})} style={{ width: '100%', padding: '9px 12px', border: `1px solid ${VA2.rule}`, borderRadius: 4, fontSize: FontSizes.xs, fontFamily: 'inherit' }} />
                </label>
                <label>
                  <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkSoft, marginBottom: 5, fontWeight: 500 }}>成交價</div>
                  <input type="number" step="0.01" value={form.entryPrice} onChange={(e) => setForm({...form, entryPrice: e.target.value})} style={{ width: '100%', padding: '9px 12px', border: `1px solid ${VA2.rule}`, borderRadius: 4, fontSize: FontSizes.xs, fontFamily: 'inherit' }} />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label>
                  <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkSoft, marginBottom: 5, fontWeight: 500 }}>帳戶</div>
                  <input value={form.trader} onChange={(e) => setForm({...form, trader: e.target.value})} placeholder="帳號（如 5571391）" style={{ width: '100%', padding: '9px 12px', border: `1px solid ${VA2.rule}`, borderRadius: 4, fontSize: FontSizes.xs, fontFamily: 'inherit' }} />
                </label>
                <label>
                  <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkSoft, marginBottom: 5, fontWeight: 500 }}>委託時間</div>
                  <input value={form.orderTime} onChange={(e) => setForm({...form, orderTime: e.target.value})} placeholder="MM/DD HH:MM" style={{ width: '100%', padding: '9px 12px', border: `1px solid ${VA2.rule}`, borderRadius: 4, fontSize: FontSizes.xs, fontFamily: 'inherit' }} />
                </label>
              </div>
              <label>
                <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkSoft, marginBottom: 5, fontWeight: 500 }}>狀態</div>
                <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} style={{ width: '100%', padding: '9px 10px', border: `1px solid ${VA2.rule}`, borderRadius: 4, fontSize: FontSizes.xs, fontFamily: 'inherit', background: '#fff' }}>
                  <option value="holding">留倉</option>
                  <option value="closed">已平</option>
                </select>
              </label>
              <label>
                <div style={{ fontSize: FontSizes.trading_lg, color: VA2.inkSoft, marginBottom: 5, fontWeight: 500 }}>交易緣由</div>
                <textarea value={form.remark} onChange={(e) => setForm({...form, remark: e.target.value})} rows="2" style={{ width: '100%', padding: '9px 12px', border: `1px solid ${VA2.rule}`, borderRadius: 4, fontSize: FontSizes.xs, fontFamily: 'inherit', resize: 'vertical' }} />
              </label>
            </div>

            {formMsg && <div style={{ marginTop: 14, padding: '8px 12px', background: '#FCE9E8', color: VA2.down, borderRadius: 4, fontSize: FontSizes.xxs }}>{formMsg}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22 }}>
              <button onClick={() => setModalOpen(false)} style={{ padding: '9px 16px', border: `1px solid ${VA2.rule}`, borderRadius: 4, background: '#fff', color: VA2.inkSoft, fontSize: FontSizes.xs, cursor: 'pointer', fontFamily: 'inherit' }}>取消</button>
              <button onClick={submitNewPosition} disabled={submitting} style={{
                padding: '9px 18px', border: 'none', borderRadius: 4,
                background: submitting ? VA2.inkFaint : VA2.accent, color: '#fff',
                fontSize: FontSizes.xs, fontWeight: 500, cursor: submitting ? 'wait' : 'pointer',
                fontFamily: 'inherit',
              }}>
                {submitting ? '送出中…' : '建立'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal — 點任一 row 開，顯示完整 fills / note / metadata（aggregated row 表格塞不下） */}
      {detailRow && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setDetailRow(null); }} style={{
          position: 'fixed', inset: 0, background: 'rgba(14,20,17,0.45)', zIndex: 101,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px',
          overflowY: 'auto',
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, width: '100%', maxWidth: 720,
            padding: '28px 32px 24px', boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 4 }}>
                  Position Detail
                </div>
                <h3 style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }}>
                  {detailRow.sym}
                </h3>
                <div style={{ marginTop: 6, fontSize: FontSizes.xxs, color: VA2.inkSoft, display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span>帳戶 <b style={{ color: VA2.ink }}>{detailRow.trader}</b></span>
                  <span style={{ color: va2Mkt(detailRow.side === 'B' ? VA2.accent : VA2.down, detailRow.side === 'B'), fontWeight: 600 }}>
                    {detailRow.side === 'B' ? '多' : '空'}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: detailRow.st === 'holding' ? VA2.warn : VA2.inkFaint }} />
                    {detailRow.st === 'holding' ? '留倉' : '已平'}
                  </span>
                </div>
              </div>
              <button onClick={() => setDetailRow(null)} style={{ background: 'transparent', border: 'none', fontSize: FontSizes.lg, color: VA2.inkFaint, cursor: 'pointer' }}>×</button>
            </div>

            {/* Headline stats */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16,
              padding: '16px 0', borderTop: `1px solid ${VA2.ruleSoft}`, borderBottom: `1px solid ${VA2.ruleSoft}`, marginBottom: 18,
            }}>
              <div>
                <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 6 }}>口數</div>
                <div style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontVariantNumeric: 'tabular-nums' }}>{detailRow.lots}</div>
              </div>
              <div>
                <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 6 }}>成交價格</div>
                <div style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontVariantNumeric: 'tabular-nums' }}>
                  {detailRow.dealPrice != null ? detailRow.dealPrice.toLocaleString() : detailRow.price}
                </div>
              </div>
              <div>
                <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 6 }}>最後取得價格</div>
                <div style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontVariantNumeric: 'tabular-nums', color: VA2.inkSoft }}>
                  {_lastMarket(detailRow) != null ? _lastMarket(detailRow).toLocaleString() : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 6 }}>損益</div>
                <div style={{ fontFamily: VA2.serif, fontSize: FontSizes.lg, fontVariantNumeric: 'tabular-nums', color: va2Mkt(detailRow.pnl >= 0 ? VA2.up : VA2.down, detailRow.pnl >= 0) }}>
                  {fmt.pnl(detailRow.pnl)}
                </div>
              </div>
            </div>

            {/* Time range */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 4 }}>成交時間</div>
              <div style={{ fontFamily: VA2.mono, fontSize: FontSizes.xs, color: VA2.inkSoft }}>{_execTimeDisplay(detailRow) || '—'}</div>
            </div>

            {/* Fills 明細 — 每筆成交 lots/price/time/pnl */}
            {Array.isArray(detailRow.fills) && detailRow.fills.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA2.inkFaint, marginBottom: 8 }}>
                  成交明細 ({detailRow.fillCount || detailRow.fills.length} 筆)
                </div>
                <div style={{ border: `1px solid ${VA2.ruleSoft}`, borderRadius: 4, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xxs }}>
                    <thead>
                      <tr style={{ background: '#FAF8F3', color: VA2.inkFaint, fontSize: FontSizes.trading_md, letterSpacing: '0.08em' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>口數</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>價格</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500 }}>時間</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>損益</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRow.fills.map((f, j) => (
                        <tr key={j} style={{ borderTop: `1px solid ${VA2.ruleSoft}` }}>
                          <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums' }}>{f.lots}</td>
                          <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums' }}>{f.price}</td>
                          <td style={{ padding: '6px 12px', fontFamily: VA2.mono, color: VA2.inkSoft }}>{(f.ot || '').replace(/\s+00:00:00$/, '')}</td>
                          <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: VA2.mono, fontVariantNumeric: 'tabular-nums', color: va2Mkt((f.pnl || 0) >= 0 ? VA2.up : VA2.down, (f.pnl || 0) >= 0) }}>
                            {fmt.pnl(f.pnl || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 交易緣由 / 完整 note — 可由任何登入者編輯（平倉列為衍生資料，不可編輯） */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA2.inkFaint }}>交易緣由</div>
                {noteEdit === null && _detailEditableIds(detailRow).length > 0 && (
                  <span onClick={() => setNoteEdit(detailRow.note || '')} style={{
                    fontSize: FontSizes.trading_lg, color: VA2.accent, cursor: 'pointer', userSelect: 'none', fontWeight: 500,
                  }}>編輯</span>
                )}
              </div>
              {noteEdit === null ? (
                <div style={{
                  padding: '12px 14px', background: '#FAF8F3', border: `1px solid ${VA2.ruleSoft}`, borderRadius: 4,
                  fontFamily: VA2.serif, fontStyle: 'italic', fontSize: FontSizes.xs, color: VA2.inkSoft,
                  lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                  {detailRow.note || '—'}
                </div>
              ) : (
                <div>
                  <textarea
                    value={noteEdit}
                    onChange={(e) => setNoteEdit(e.target.value)}
                    rows={3}
                    placeholder="輸入這筆交易的緣由/邏輯…"
                    autoFocus
                    style={{
                      width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                      border: `1px solid ${VA2.rule}`, borderRadius: 4,
                      fontSize: FontSizes.xs, fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => setNoteEdit(null)} disabled={noteSaving} style={{
                      padding: '7px 14px', border: `1px solid ${VA2.rule}`, borderRadius: 4, background: 'transparent',
                      fontSize: FontSizes.xxs, color: VA2.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                    }}>取消</button>
                    <button onClick={saveDetailNote} disabled={noteSaving} style={{
                      padding: '7px 14px', border: 'none', borderRadius: 4, background: VA2.accent,
                      fontSize: FontSizes.xxs, color: '#fff', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                      opacity: noteSaving ? 0.6 : 1,
                    }}>{noteSaving ? '儲存中…' : '儲存'}</button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

window.VariantA2 = VariantA2;
window.TradingSidebar = TradingSidebar;
window.TraderRightRail = TraderRightRail;
window.WorkspaceHeader = WorkspaceHeader;
